'use strict'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import axios from 'axios'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@material-ui/core'
import WarningIcon from '@material-ui/icons/Warning'
import LockIcon from '@material-ui/icons/Lock'
import OpenInNewIcon from '@material-ui/icons/OpenInNew'
import { getCrewColor, getRouteColor } from './crewColors'
import { routeDisplayName } from '../routeDisplayName'
import {
  parseYmd, toYmd, addDays, getMonday,
  getWeekTypeForDate, getDayName, normalizeDaysForRow,
  rowWeekType,
  isDueOnDate, countDueOccurrences, getFrequencyLabelFromCode,
  WORK_DAYS
} from './scheduleUtils'
import {
  MAP_CONTAINER_STYLE, HMI_YARD, DEFAULT_CENTER, DEFAULT_ZOOM,
  haversineMiles, formatMinutes
} from './mapUtils'
import { useTerritory } from './TerritoryContext'
import useRouteContext from './useRouteContext'
import useCapacityMatrix from './useCapacityMatrix'
import useFleetRoutes from './useFleetRoutes'
import useReviewNavigator from './useReviewNavigator'
import useMapFilters from './useMapFilters'
import {
  applyBasemapProvider,
  getLatestMaricopaProvider,
  googleHybridProvider
} from '../../HerbicideSchedule/v3/maricopaBasemap'
import MapToolbar from './MapToolbar'
import PropertyPopover from './PropertyPopover'
import CapacityDrawer from './CapacityDrawer'
import LongCyclePlannerDrawer from './LongCyclePlannerDrawer'
import RouteSidebar from './RouteSidebar'
import { WeekVizProvider, WeekVizOverlays, WeekVizSidebar } from './WeekViz/WeekViz'
import { PropertyDetailDrawer, normalizeRoutingRow } from '../../PropertyDetail'
import AutoOptimizeRunReport from './AutoOptimizeRunReport'
import TimeAuditModal from './TimeAuditModal'
import useAutoOptimize from './useAutoOptimize'

// Helper: wraps children in WeekVizProvider only when `enabled`.  Avoids
// fetching week-stops data in day mode while keeping the JSX structure flat.
function ConditionalWeekVizProvider ({ enabled, providerProps, children }) {
  if (!enabled) return <>{children}</>
  return <WeekVizProvider {...providerProps}>{children}</WeekVizProvider>
}

const WEEKLY_OT_THRESHOLD_MIN = 2400
const DAILY_OT_THRESHOLD_MIN = 480
const SUGGESTION_PIN_COLORS = [
  '#E91E63', '#9C27B0', '#3F51B5', '#00BCD4', '#FF9800',
  '#8BC34A', '#795548', '#607D8B', '#F44336', '#009688'
]
const areSameStringArrays = (left = [], right = []) => {
  if (left.length !== right.length) return false
  const l = [...left].map(item => String(item)).sort()
  const r = [...right].map(item => String(item)).sort()
  return l.every((item, idx) => item === r[idx])
}

const createSuggestionIcon = (color, label) => L.divIcon({
  className: 'territory-workbench-suggestion-marker',
  html: `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="40" viewBox="0 0 28 40"><path d="M14 0C6.27 0 0 6.27 0 14c0 10.5 14 26 14 26s14-15.5 14-26C28 6.27 21.73 0 14 0z" fill="${color}" stroke="#fff" stroke-width="1.5"/><text x="14" y="18" text-anchor="middle" fill="#fff" font-size="12" font-weight="700" font-family="Arial,sans-serif">${label}</text></svg>`,
  iconSize: [28, 40],
  iconAnchor: [14, 40]
})

const createCentroidIcon = (color) => L.divIcon({
  className: 'territory-workbench-centroid-marker',
  html: `<div style="width:0;height:0;border-left:8px solid transparent;border-right:8px solid transparent;border-bottom:16px solid ${color};filter:drop-shadow(0 0 1px #000) drop-shadow(0 0 1px #000);"></div>`,
  iconSize: [16, 16],
  iconAnchor: [8, 8]
})

const yardIcon = L.divIcon({
  className: 'territory-workbench-yard-marker',
  html: '<div style="width:22px;height:22px;background:#FF6F00;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.35);clip-path:polygon(50% 0,100% 42%,82% 42%,82% 100%,18% 100%,18% 42%,0 42%);"></div>',
  iconSize: [24, 24],
  iconAnchor: [12, 22]
})

const toLatLng = (point) => [point.lat, point.lng]

const bindTextTooltip = (layer, text) => {
  if (!text || typeof document === 'undefined') return
  const node = document.createElement('span')
  node.textContent = String(text)
  layer.bindTooltip(node, { direction: 'top', opacity: 0.9 })
}

const bindPropertyTooltip = (layer, props = {}) => {
  if (typeof document === 'undefined') return
  const node = document.createElement('div')
  node.style.maxWidth = '260px'

  const name = document.createElement('div')
  name.textContent = props.propertyName || props.name || `Aspire #${props.aspirePropertyId || ''}`
  name.style.fontWeight = '700'
  node.appendChild(name)

  const lines = [
    [props.address, props.city].filter(Boolean).join(', '),
    props.aspirePropertyId ? `Aspire #${props.aspirePropertyId}` : '',
    routeDisplayName(props),
    props.frequencyCode ? `Freq: ${props.frequencyCode}` : '',
    props.estimatedMinutesOnSite ? `Est: ${formatMinutes(props.estimatedMinutesOnSite)}` : ''
  ].filter(Boolean)

  lines.forEach(line => {
    const row = document.createElement('div')
    row.textContent = line
    row.style.fontSize = '12px'
    row.style.color = '#444'
    node.appendChild(row)
  })

  layer.bindTooltip(node, { direction: 'top', opacity: 0.95 })
}

const adaptMapFeatureToRoutingProperty = (feature) => {
  const props = feature?.properties || {}
  const [lng, lat] = feature?.geometry?.coordinates || []
  const estimatedHours = Number(props.estimatedMinutesOnSite || 0) / 60 || Number(props.estimatedHours || 0)
  const medianOnSiteHours = Number(props.medianOnSiteMinutes || 0) / 60 || Number(props.medianOnSiteHours || 0)
  const crewName = props.assignedRouteName || props.assignedCrewName || ''

  return normalizeRoutingRow({
    ...props,
    name: props.propertyName || props.name || `Aspire #${props.aspirePropertyId || props.propertyId || ''}`,
    propertyId: props.aspirePropertyId || props.propertyId,
    aspirePropertyId: props.aspirePropertyId || props.propertyId,
    address1: props.address || props.address1 || '',
    address2: props.city || props.address2 || '',
    latitude: props.latitude ?? lat,
    longitude: props.longitude ?? lng,
    estimatedHours,
    avgOnSiteHours: props.avgOnSiteHours || medianOnSiteHours || estimatedHours,
    medianOnSiteHours: props.medianOnSiteHours || medianOnSiteHours,
    prescribedManHours: props.prescribedManHours ?? null,
    scheduledDays: props.scheduledDays || props.daysOfWeek || (props.dayOfWeek ? [props.dayOfWeek] : []),
    futureVisits: props.futureVisits || [],
    primaryCrewLeader: crewName
      ? { name: crewName, crewLeaderName: crewName }
      : props.primaryCrewLeader,
    completedOccurrences: props.completedOccurrences || 0,
    futureTicketCount: props.futureTicketCount || props.futureVisits?.length || 0
  })
}

const TerritoryLeafletMap = ({
  propertyFeatures,
  selectedPropertyId,
  suggestionCandidates,
  showSuggestionPins,
  focusedRouteStops,
  focusedRoutePath,
  isFleetMode,
  showFleetRoutes,
  fleetRoutes,
  centroidFeatures,
  routeIds,
  crewIds,
  getMarkerStyle,
  handlePropertySelect,
  setRouteFilterId,
  onMapLoad
}) => {
  const [mapContainerNode, setMapContainerNode] = useState(null)
  const mapRef = useRef(null)
  const baseTileLayerRef = useRef(null)
  const maricopaTileLayerRef = useRef(null)
  const dataLayerRef = useRef(null)

  const handleMapContainerRef = useCallback((node) => {
    setMapContainerNode(node)
  }, [])

  useEffect(() => {
    if (!mapContainerNode || mapRef.current) return

    const map = L.map(mapContainerNode, {
      center: [DEFAULT_CENTER.lat, DEFAULT_CENTER.lng],
      zoom: DEFAULT_ZOOM,
      maxZoom: 28,
      zoomControl: true,
      attributionControl: false
    })

    applyBasemapProvider({
      map,
      tileLayerRef: baseTileLayerRef,
      provider: googleHybridProvider
    })
    if (baseTileLayerRef.current?.setZIndex) baseTileLayerRef.current.setZIndex(0)

    mapRef.current = map
    onMapLoad(map)

    let cancelled = false
    getLatestMaricopaProvider()
      .then((provider) => {
        if (cancelled || !mapRef.current) return
        const maricopaLayer = applyBasemapProvider({
          map,
          tileLayerRef: maricopaTileLayerRef,
          provider
        })
        if (maricopaLayer?.setZIndex) maricopaLayer.setZIndex(1)
      })
      .catch((err) => {
        console.warn('Failed to load latest Maricopa basemap for territory workbench:', err)
      })

    setTimeout(() => {
      try { map.invalidateSize() } catch (_) {}
    }, 150)

    return () => {
      cancelled = true
      if (dataLayerRef.current) {
        try { map.removeLayer(dataLayerRef.current) } catch (_) {}
        dataLayerRef.current = null
      }
      if (maricopaTileLayerRef.current) {
        try { map.removeLayer(maricopaTileLayerRef.current) } catch (_) {}
        maricopaTileLayerRef.current = null
      }
      if (baseTileLayerRef.current) {
        try { map.removeLayer(baseTileLayerRef.current) } catch (_) {}
        baseTileLayerRef.current = null
      }
      try { map.remove() } catch (_) {}
      mapRef.current = null
      onMapLoad(null)
    }
  }, [mapContainerNode, onMapLoad])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    if (dataLayerRef.current) {
      try { map.removeLayer(dataLayerRef.current) } catch (_) {}
    }
    const dataLayer = L.layerGroup().addTo(map)
    dataLayerRef.current = dataLayer

    if (focusedRoutePath.length > 1 && !isFleetMode) {
      L.polyline(focusedRoutePath.map(toLatLng), {
        color: '#00BCD4',
        opacity: 0.9,
        weight: 4
      }).addTo(dataLayer)
    }

    if (isFleetMode && showFleetRoutes) {
      fleetRoutes.forEach((fr) => {
        if (!fr.path || fr.path.length <= 1) return
        L.polyline(fr.path.map(toLatLng), {
          color: fr.routeId
            ? (getRouteColor(fr.routeId, routeIds) || getCrewColor(fr.crewId, crewIds))
            : getCrewColor(fr.crewId, crewIds),
          opacity: 0.8,
          weight: 3
        }).addTo(dataLayer)
      })
    }

    const renderedPropertyIds = new Set()

    propertyFeatures.forEach((feature) => {
      const [lng, lat] = feature.geometry?.coordinates || []
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return
      const props = feature.properties || {}
      if (props.aspirePropertyId) renderedPropertyIds.add(Number(props.aspirePropertyId))
      const marker = L.circleMarker([lat, lng], getMarkerStyle(feature))
        .addTo(dataLayer)
        .on('click', () => handlePropertySelect(feature))
      bindPropertyTooltip(marker, props)
      if (Number(props.aspirePropertyId) === Number(selectedPropertyId)) marker.bringToFront()
    })

    const routeStopMarkerDefs = isFleetMode
      ? fleetRoutes.flatMap((fr) => (fr.stops || []).map((stop) => ({
          stop,
          routeId: fr.routeId,
          crewId: fr.crewId,
          routeName: fr.routeName,
          crewName: fr.crewName,
          color: fr.routeId
            ? (getRouteColor(fr.routeId, routeIds) || getCrewColor(fr.crewId, crewIds))
            : getCrewColor(fr.crewId, crewIds)
        })))
      : (focusedRouteStops || []).map((stop) => ({
          stop,
          routeId: null,
          crewId: null,
          routeName: '',
          crewName: '',
          color: '#00BCD4'
        }))

    if (routeStopMarkerDefs.length) {
      routeStopMarkerDefs.forEach(({ stop, routeId, crewId, routeName, crewName, color }) => {
        const id = Number(stop.aspirePropertyId)
        if (renderedPropertyIds.has(id)) return
        const lat = Number(stop.latitude)
        const lng = Number(stop.longitude)
        if (!Number.isFinite(lat) || !Number.isFinite(lng) || lat === 0 || lng === 0) return
        const feature = {
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [lng, lat] },
          properties: {
            featureType: 'property',
            aspirePropertyId: id,
            propertyName: stop.propertyName || `Aspire #${id}`,
            address: stop.address || '',
            assignedRouteId: routeId || null,
            assignedCrewId: crewId || null,
            assignedRouteName: routeName || stop.routeName || '',
            assignedCrewName: crewName || stop.crewLeaderName || '',
            estimatedMinutesOnSite: stop.serviceMinutes || 0,
            prescribedManHours: stop.prescribedManHours ?? null,
            _routeStopOnly: true
          }
        }
        const marker = L.circleMarker([lat, lng], {
          radius: Number(stop.order) === 0 ? 11 : 9,
          fillColor: color || '#00BCD4',
          fillOpacity: 0.95,
          color: '#ffffff',
          weight: 3,
          opacity: 1
        })
          .addTo(dataLayer)
          .on('click', () => handlePropertySelect(feature))
        bindPropertyTooltip(marker, feature.properties)
        if (id === Number(selectedPropertyId)) marker.bringToFront()
        renderedPropertyIds.add(id)
      })
    }

    if (showSuggestionPins) {
      suggestionCandidates.forEach((candidate, idx) => {
        const [lng, lat] = candidate.feature?.geometry?.coordinates || []
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return
        const color = SUGGESTION_PIN_COLORS[idx % SUGGESTION_PIN_COLORS.length]
        const marker = L.marker([lat, lng], {
          icon: createSuggestionIcon(color, String(idx + 1)),
          zIndexOffset: 1000 + idx,
          title: `#${idx + 1} ${candidate.propertyName || ''}`
        })
          .addTo(dataLayer)
          .on('click', () => handlePropertySelect(candidate.feature))
        bindTextTooltip(marker, `#${idx + 1} ${candidate.propertyName || ''}`)
      })
    }

    centroidFeatures.forEach((feature) => {
      const [lng, lat] = feature.geometry?.coordinates || []
      const props = feature.properties || {}
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return
      const centroidId = props.routeId || props.crewId
      if (!centroidId) return
      const centroidName = props.routeName || props.crewName
      const color = getRouteColor(centroidId, routeIds) || getCrewColor(centroidId, crewIds)
      L.marker([lat, lng], {
        icon: createCentroidIcon(color),
        title: `${centroidName || centroidId} (centroid)`,
        zIndexOffset: 900
      })
        .addTo(dataLayer)
        .on('click', () => setRouteFilterId(String(centroidId)))
    })

    L.marker([HMI_YARD.lat, HMI_YARD.lng], {
      icon: yardIcon,
      title: HMI_YARD.label,
      zIndexOffset: 999
    }).addTo(dataLayer)
  }, [
    centroidFeatures,
    crewIds,
    fleetRoutes,
    focusedRouteStops,
    focusedRoutePath,
    getMarkerStyle,
    handlePropertySelect,
    isFleetMode,
    propertyFeatures,
    routeIds,
    selectedPropertyId,
    setRouteFilterId,
    showFleetRoutes,
    showSuggestionPins,
    suggestionCandidates
  ])

  return (
    <div
      ref={handleMapContainerRef}
      style={MAP_CONTAINER_STYLE}
    />
  )
}

const MapStatsStrip = ({ stats, propertyCount }) => {
  if (!stats) return null
  
  const items = [
    { label: 'Properties', value: propertyCount || stats.totalStops || 0 },
    { label: 'Service Time', value: formatMinutes(stats.totalServiceMin) },
    { label: 'Drive Time', value: formatMinutes(stats.totalDriveMin) },
    { label: 'Total Miles', value: `${stats.totalMiles?.toFixed(1) || 0} mi` },
    { label: 'Total Time', value: formatMinutes(stats.totalTotalMin) },
    { label: 'Est. OT', value: formatMinutes(stats.totalOtMin), warn: stats.crewsInOt > 0 }
  ]

  return (
    <div style={{
      display: 'flex', gap: 24, padding: '12px 24px', 
      background: 'var(--df-surface)', borderBottom: '1px solid var(--df-line)',
      alignItems: 'center', flexShrink: 0, overflowX: 'auto'
    }}>
      {items.map(item => (
        <div key={item.label} style={{ display: 'flex', flexDirection: 'column', minWidth: 'fit-content' }}>
          <span style={{ fontSize: 18, fontWeight: 800, color: item.warn ? 'var(--df-danger)' : 'var(--df-ink)', lineHeight: 1 }}>
            {item.value}
          </span>
          <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--df-ink-3)', marginTop: 2 }}>
            {item.label}
          </span>
        </div>
      ))}
    </div>
  )
}

export default function MapTab ({ url, selectedScenarioId, isScenarioEditable, crews, crewIds, routes, routeIds, onSnackbar, reviewRequest, onOpenRefine }) {
  const { scenarioMetrics, loadingMetrics, fetchMetrics: _ctxFetchMetrics } = useTerritory()
  const fetchScenarioMetrics = useCallback(() => _ctxFetchMetrics(selectedScenarioId || null), [_ctxFetchMetrics, selectedScenarioId])

  // ── Core map state ─────────────────────────────────────────
  const [features, setFeatures] = useState([])
  const [meta, setMeta] = useState(null)
  const [loading, setLoading] = useState(false)
  const [filterAnchorEl, setFilterAnchorEl] = useState(null)
  const [legendOpen, setLegendOpen] = useState(false)
  const [showCentroids, setShowCentroids] = useState(true)
  const [mapRef, setMapRef] = useState(null)

  // ── Property selection / assignment state ──────────────────
  const [selectedPropertyId, setSelectedPropertyId] = useState(null)
  const [selectedPropertyFallbackFeature, setSelectedPropertyFallbackFeature] = useState(null)
  const [assignmentRouteId, setAssignmentRouteId] = useState('')
  const [anchorCrewId, setAnchorCrewId] = useState('')
  const [applyingMove, setApplyingMove] = useState(false)
  const [, setApplyingAnchor] = useState(false)
  const [suggestingCrew, setSuggestingCrew] = useState(false)
  const [, setNearestCrewSuggestion] = useState(null)

  // ── Focus state (crew/route/date/mode) ─────────────────────
  const [focusCrewId, setFocusCrewId] = useState('')
  const [focusRouteId, setFocusRouteId] = useState('__ALL__')
  const [planningMode, setPlanningMode] = useState('day') // day | week | two-week
  const [focusDate, setFocusDate] = useState(() => toYmd(new Date()))
  const [contextWeekType, setContextWeekType] = useState('ALL') // ALL | A | B
  const [scheduleDay, setScheduleDay] = useState('')
  const [scheduleWeekType, setScheduleWeekType] = useState('')
  const [hoursMode, setHoursMode] = useState('estimated') // 'estimated' | 'actual'

  // ── UI toggles ─────────────────────────────────────────────
  const [candidatesDueOnly, setCandidatesDueOnly] = useState(false)
  const [showSuggestionPins, setShowSuggestionPins] = useState(false)
  const [showFleetRoutes, setShowFleetRoutes] = useState(true)
  const [routeActionPending, setRouteActionPending] = useState(false)
  const [bestFitData, setBestFitData] = useState(null)
  const [bestFitLoading, setBestFitLoading] = useState(false)
  const [bestFitOpen, setBestFitOpen] = useState(true)
  const [drawerOpen, setDrawerOpen] = useState(false)
  // Long-cycle planner drawer (scenario-agnostic — live-level rows)
  const [longCycleOpen, setLongCycleOpen] = useState(false)
  const [propertyDetailOpen, setPropertyDetailOpen] = useState(false)
  const [hydratedPropertyDetail, setHydratedPropertyDetail] = useState(null)
  // ── Confirmation dialog state ──────────────────────────────
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', type: '', details: null })
  const confirmResolverRef = useRef(null)

  // options: { confirmLabel, cancelLabel } — used by generic (non move-conflict) confirms,
  // e.g. auto-optimize start/cancel, so they can show copy other than "Move Anyway".
  const requestConfirmation = useCallback((title, message, type = 'generic', details = null, options = null) => {
    return new Promise((resolve) => {
      confirmResolverRef.current = resolve
      setConfirmDialog({ open: true, title, message, type, details, options })
    })
  }, [])

  const handleConfirmDialogClose = useCallback((confirmed) => {
    setConfirmDialog(prev => ({ ...prev, open: false }))
    if (confirmResolverRef.current) {
      confirmResolverRef.current(confirmed)
      confirmResolverRef.current = null
    }
  }, [])

  const fetchMapData = useCallback(async () => {
    setLoading(true)
    try {
      const endpoint = selectedScenarioId
        ? `${url}/api/hmi/territory/scenarios/${selectedScenarioId}/map-data`
        : `${url}/api/hmi/territory/map-data`
      const response = await axios.get(endpoint)
      setFeatures(response.data?.featureCollection?.features || [])
      setMeta(response.data?.meta || null)
    } catch (err) {
      console.error('Error loading map data:', err)
      if (onSnackbar) onSnackbar(err.response?.data?.message || err.message || 'Failed to load map data', 'error')
    } finally {
      setLoading(false)
    }
  }, [url, selectedScenarioId, onSnackbar])

  useEffect(() => {
    fetchMapData()
  }, [fetchMapData])

  // ── Hooks: route context, capacity, fleet routes, review navigator ──
  const {
    focusedRoute, loadingFocusedRoute, fetchFocusedRoute,
    filteredContextDates,
    focusContextStats, loadingFocusContextStats, fetchFocusContextStats,
    weeklyCrewStats
  } = useRouteContext({ url, selectedScenarioId, focusCrewId, focusDate, planningMode, contextWeekType })

  // Auto-Optimize run lifecycle (global VROOM reassignment) — mounted here (Task 9)
  // so it's available on the Map tab. onApplied mirrors the post-move refresh used
  // by postMoveWithApprovalCheck call sites below and MUST be identity-stable: the
  // hook's polling effect lists it in its deps, and an inline arrow would tear
  // down/rebuild the interval on every render (setAutoRun re-renders every 2.5s poll tick).
  const handleAutoOptApplied = useCallback(() => {
    fetchMapData()
    fetchScenarioMetrics()
    fetchFocusContextStats()
    fetchFocusedRoute()
  }, [fetchMapData, fetchScenarioMetrics, fetchFocusContextStats, fetchFocusedRoute])
  const autoOpt = useAutoOptimize({
    url,
    scenarioId: selectedScenarioId,
    date: focusDate,
    onSnackbar,
    onApplied: handleAutoOptApplied
  })

  const availableRoutes = useMemo(() => {
    if (Array.isArray(routes) && routes.length) {
      return routes
        .map(item => ({ routeId: String(item._id), routeName: item.name || String(item._id), crewId: item.crewId ? String(item.crewId) : null }))
        .sort((a, b) => a.routeName.localeCompare(b.routeName))
    }
    return []
  }, [routes])

  const availableCrews = useMemo(() => {
    if (Array.isArray(crews) && crews.length) {
      return crews
        .map(item => ({ crewId: String(item._id), crewName: item.name || item.foremanName || String(item._id) }))
        .sort((a, b) => a.crewName.localeCompare(b.crewName))
    }

    const centroidCrewMap = new Map()
    features
      .filter(item => item.properties?.featureType === 'crewCentroid')
      .forEach(item => {
        const crewId = String(item.properties?.crewId || '')
        if (!crewId) return
        centroidCrewMap.set(crewId, item.properties?.crewName || crewId)
      })

    return Array.from(centroidCrewMap.entries())
      .map(([crewId, crewName]) => ({ crewId, crewName }))
      .sort((a, b) => a.crewName.localeCompare(b.crewName))
  }, [crews, features])

  // For dropdowns: prefer routes, fallback to crews
  const assignmentOptions = useMemo(() => {
    if (availableRoutes.length) return availableRoutes.map(r => ({ id: r.routeId, name: r.routeName }))
    return availableCrews.map(c => ({ id: c.crewId, name: c.crewName }))
  }, [availableRoutes, availableCrews])

  // Name lookup by route/crew ID for resolving date-specific override names
  const nameById = useMemo(() => {
    const map = new Map()
    availableRoutes.forEach(r => { map.set(r.routeId, r.routeName) })
    availableCrews.forEach(c => { map.set(c.crewId, c.crewName) })
    return map
  }, [availableRoutes, availableCrews])

  const isRouteMode = availableRoutes.length > 0
  const isFleetMode = focusRouteId === '__ALL__'

  // ── Hook: capacity matrix ─────────────────────────────────
  const {
    capacityMatrix, setCapacityMatrix, capacityLoading,
    fleetDayStats, fleetAvgWeeklyMinutes, capacityByRoute
  } = useCapacityMatrix({ url, selectedScenarioId, focusDate, isFleetMode, scenarioMetrics })

  // ── Hook: fleet routes ────────────────────────────────────
  const {
    fleetRoutes, fleetRoutesLoading, fetchFleetRoutes
  } = useFleetRoutes({ url, selectedScenarioId, isFleetMode, focusDate, showFleetRoutes, availableRoutes, availableCrews })

  // ── Hook: review navigator ────────────────────────────────
  const {
    approvedRouteDays, setApprovedRouteDays, approvedSet, fetchApprovedRouteDays,
    reviewCombos, filteredReviewCombos,
    reviewNavOpen, setReviewNavOpen,
    reviewComboIdx, setReviewComboIdx,
    skipApproved, setSkipApproved,
    reviewNavNext, reviewNavPrev, navigateToCombo
  } = useReviewNavigator({ url, selectedScenarioId, capacityMatrix, setFocusCrewId, setFocusRouteId, setFocusDate })

  useEffect(() => {
    if (focusRouteId === '__ALL__') return
    if (isRouteMode && availableRoutes.length) {
      if (focusRouteId && availableRoutes.some(r => r.routeId === focusRouteId)) return
      // Previously-selected route no longer valid — fall back to fleet mode
      setFocusRouteId('__ALL__')
      setFocusCrewId('')
      return
    }
    if (!availableCrews.length) {
      setFocusCrewId('')
      setFocusRouteId('__ALL__')
      return
    }
    if (focusCrewId && availableCrews.some(item => item.crewId === focusCrewId)) return
    // Previously-selected crew no longer valid — fall back to fleet mode
    setFocusRouteId('__ALL__')
    setFocusCrewId('')
  }, [availableCrews, availableRoutes, focusCrewId, focusRouteId, isRouteMode])

  // Resolve per-date crew/route from futureVisitCrews (Aspire one-off assignments)
  const effectiveCrewByProperty = useMemo(() => {
    if (!focusDate) return new Map()
    const dateStr = focusDate // already YYYY-MM-DD
    const map = new Map()
    features.forEach(f => {
      const p = f.properties
      if (p?.featureType !== 'property' || !p.futureVisitCrews?.length) return
      const match = p.futureVisitCrews.find(v => v.date === dateStr)
      if (match) {
        map.set(Number(p.aspirePropertyId), {
          crewId: match.crewId || null,
          routeId: match.routeId || null
        })
      }
    })
    return map
  }, [features, focusDate])

  // Track on-route property IDs (must be before propertyFeatures for showRouteStops filter)
  const routeStopIds = useMemo(() => {
    const stops = focusedRoute?.stops || []
    return new Set(stops.map(s => Number(s.aspirePropertyId)).filter(Number.isFinite))
  }, [focusedRoute])

  const fleetRouteStopIds = useMemo(() => {
    const ids = new Set()
    fleetRoutes.forEach(fr => {
      ;(fr.stops || []).forEach(stop => {
        const id = Number(stop.aspirePropertyId)
        if (Number.isFinite(id)) ids.add(id)
      })
    })
    return ids
  }, [fleetRoutes])

  // ── Hook: map filtering pipeline ──────────────────────────
  const {
    propertyFeatures, allPropertyFeatures, activeFilterCount, resetFilters,
    showVipOnly, setShowVipOnly,
    showAnchorsOnly, setShowAnchorsOnly,
    showLockedOnly, setShowLockedOnly,
    routeFilterId, setRouteFilterId,
    showDueOnly, setShowDueOnly,
    frequencyFocus, setFrequencyFocus,
    showIrregularMtc, setShowIrregularMtc,
    showRouteStops, setShowRouteStops,
    showUnassigned, setShowUnassigned,
    fleetTodayOnly, setFleetTodayOnly
  } = useMapFilters({
    features, focusDate, focusCrewId, isFleetMode,
    effectiveCrewByProperty, routeStopIds, fleetRouteStopIds,
    filteredContextDates, showFleetRoutes
  })

  const allPropertyDetailRows = useMemo(
    () => allPropertyFeatures.map(adaptMapFeatureToRoutingProperty),
    [allPropertyFeatures]
  )

  const centroidFeatures = useMemo(() => {
    if (!showCentroids) return []
    if (routeFilterId === 'unassigned') return []
    let centroids = features.filter(f => f.properties?.featureType === 'routeCentroid' || f.properties?.featureType === 'crewCentroid')
    if (routeFilterId) centroids = centroids.filter(f => String(f.properties.routeId || f.properties.crewId || '') === String(routeFilterId))
    return centroids
  }, [features, showCentroids, routeFilterId])

  const selectedPropertyFeature = useMemo(() => {
    if (!selectedPropertyId) return null
    const existing = allPropertyFeatures.find(f => Number(f.properties?.aspirePropertyId) === Number(selectedPropertyId))
    if (existing) return existing
    if (
      selectedPropertyFallbackFeature?.properties?.featureType === 'property' &&
      Number(selectedPropertyFallbackFeature.properties?.aspirePropertyId) === Number(selectedPropertyId)
    ) {
      return selectedPropertyFallbackFeature
    }
    const stop = (focusedRoute?.stops || []).find(s => Number(s.aspirePropertyId) === Number(selectedPropertyId))
    const lat = Number(stop?.latitude)
    const lng = Number(stop?.longitude)
    if (!stop || !Number.isFinite(lat) || !Number.isFinite(lng)) return null
    const routeName = isRouteMode
      ? (availableRoutes.find(r => r.routeId === focusRouteId)?.routeName || '')
      : ''
    const crewName = isRouteMode
      ? (availableRoutes.find(r => r.routeId === focusRouteId)?.routeName || '')
      : (availableCrews.find(item => item.crewId === focusCrewId)?.crewName || '')
    return {
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [lng, lat] },
      properties: {
        featureType: 'property',
        aspirePropertyId: Number(stop.aspirePropertyId),
        propertyName: stop.propertyName || `Aspire #${stop.aspirePropertyId}`,
        address: stop.address || '',
        assignedRouteName: routeName,
        assignedCrewName: crewName,
        assignedRouteId: isRouteMode ? focusRouteId : null,
        assignedCrewId: focusCrewId || null,
        estimatedMinutesOnSite: stop.serviceMinutes || 0,
        prescribedManHours: stop.prescribedManHours ?? null,
        medianOnSiteMinutes: stop.medianServiceMinutes || 0,
        _routeStopOnly: true
      }
    }
  }, [allPropertyFeatures, availableCrews, availableRoutes, focusCrewId, focusRouteId, focusedRoute, isRouteMode, selectedPropertyFallbackFeature, selectedPropertyId])

  const allCentroidFeatures = useMemo(
    () => features.filter(f => f.properties?.featureType === 'routeCentroid' || f.properties?.featureType === 'crewCentroid'),
    [features]
  )

  const centroidByCrewId = useMemo(() => {
    const map = new Map()
    allCentroidFeatures.forEach(feature => {
      const id = String(feature.properties?.routeId || feature.properties?.crewId || '')
      const [lng, lat] = feature.geometry?.coordinates || []
      if (!id || !Number.isFinite(lat) || !Number.isFinite(lng)) return
      map.set(id, { lat, lng })
    })
    return map
  }, [allCentroidFeatures])

  const crewMetricsById = useMemo(() => {
    const map = new Map()
    // Prefer route-level metrics, fallback to crew-level
    const routesList = scenarioMetrics?.routes || []
    if (routesList.length) {
      routesList.forEach(item => {
        if (!item?.routeId) return
        map.set(String(item.routeId), item)
      })
    }
    const crewsList = scenarioMetrics?.crews || []
    crewsList.forEach(item => {
      if (!item?.crewId || item.crewId === 'unassigned') return
      if (!map.has(String(item.crewId))) {
        map.set(String(item.crewId), item)
      }
    })
    return map
  }, [scenarioMetrics])

  useEffect(() => {
    if (isFleetMode) setLegendOpen(true)
  }, [isFleetMode])

  useEffect(() => {
    if (!selectedPropertyId) return
    if (selectedPropertyFeature) return
    setSelectedPropertyId(null)
  }, [selectedPropertyFeature, selectedPropertyId])

  useEffect(() => {
    if (!selectedPropertyFeature) {
      setAssignmentRouteId('')
      setAnchorCrewId('')
      setScheduleDay('')
      setScheduleWeekType('')
      setNearestCrewSuggestion(null)
      return
    }

    const props = selectedPropertyFeature.properties || {}
    const dateOvr = effectiveCrewByProperty.get(Number(props.aspirePropertyId))
    const effRouteId = dateOvr?.routeId || props.assignedRouteId
    const effCrewId = dateOvr?.crewId || props.assignedCrewId
    const assignedId = effRouteId
      ? String(effRouteId)
      : (effCrewId ? String(effCrewId) : '')
    setAssignmentRouteId(assignedId)
    setAnchorCrewId(assignedId)
    setScheduleDay('')
    setScheduleWeekType('')
    setNearestCrewSuggestion(null)
  }, [effectiveCrewByProperty, selectedPropertyFeature])

  const nearbyProperties = useMemo(() => {
    if (!selectedPropertyFeature) return []

    const [selectedLng, selectedLat] = selectedPropertyFeature.geometry.coordinates
    if (!Number.isFinite(selectedLat) || !Number.isFinite(selectedLng)) return []

    return allPropertyFeatures
      .filter(item => Number(item.properties?.aspirePropertyId) !== Number(selectedPropertyFeature.properties?.aspirePropertyId))
      .map(item => {
        const [lng, lat] = item.geometry.coordinates
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
        const distanceMiles = haversineMiles(selectedLat, selectedLng, lat, lng)
        return {
          feature: item,
          distanceMiles
        }
      })
      .filter(Boolean)
      .sort((a, b) => a.distanceMiles - b.distanceMiles)
      .slice(0, 10)
  }, [allPropertyFeatures, selectedPropertyFeature])

  const routeLegend = useMemo(() => {
    const legendMap = new Map()
    propertyFeatures.forEach(f => {
      const p = f.properties
      const dateOverride = effectiveCrewByProperty.get(Number(p.aspirePropertyId))
      const rid = dateOverride?.routeId || p.assignedRouteId
      if (rid) {
        const existing = legendMap.get(rid)
        if (existing) {
          existing.count += 1
        } else {
          legendMap.set(rid, { name: nameById.get(String(rid)) || p.assignedRouteName || rid, count: 1 })
        }
      } else {
        const cid = dateOverride?.crewId || p.assignedCrewId
        if (cid) {
          const existing = legendMap.get(cid)
          if (existing) {
            existing.count += 1
          } else {
            legendMap.set(cid, { name: nameById.get(String(cid)) || p.assignedCrewName || cid, count: 1 })
          }
        }
      }
    })
    return Array.from(legendMap.entries())
      .map(([id, { name, count }]) => [id, name, count])
      .sort((a, b) => a[1].localeCompare(b[1]))
  }, [effectiveCrewByProperty, nameById, propertyFeatures])

  const unassignedCount = useMemo(() => {
    return allPropertyFeatures.reduce((count, feature) => {
      const p = feature.properties || {}
      const dateOverride = effectiveCrewByProperty.get(Number(p.aspirePropertyId))
      const routeId = dateOverride?.routeId || p.assignedRouteId || null
      const crewId = dateOverride?.crewId || p.assignedCrewId || null
      return (!routeId && !crewId) ? count + 1 : count
    }, 0)
  }, [allPropertyFeatures, effectiveCrewByProperty])

  const unassignedPropertyFeatures = useMemo(() => {
    return allPropertyFeatures.filter(feature => {
      const p = feature.properties || {}
      const dateOverride = effectiveCrewByProperty.get(Number(p.aspirePropertyId))
      const routeId = dateOverride?.routeId || p.assignedRouteId || null
      const crewId = dateOverride?.crewId || p.assignedCrewId || null
      return !routeId && !crewId
    })
  }, [allPropertyFeatures, effectiveCrewByProperty])

  const visibleMapPropertyFeatures = useMemo(() => {
    if (planningMode === 'day') return propertyFeatures
    return (routeFilterId === 'unassigned' || showUnassigned) ? unassignedPropertyFeatures : []
  }, [planningMode, propertyFeatures, routeFilterId, showUnassigned, unassignedPropertyFeatures])

  // Live fleet roster summary, derived from current property assignments — no scenario required.
  // Aggregates the entire roster visible on the map (matches the legend), not just "due today".
  const liveFleetDayStats = useMemo(() => {
    if (!isFleetMode) return null
    const dayName = focusDate ? getDayName(parseYmd(focusDate)) : null

    const crewDayMap = new Map()
    let totalStops = 0
    let totalServiceMin = 0
    let unassignedStops = 0
    let unassignedServiceMin = 0

    propertyFeatures.forEach(f => {
      const p = f.properties
      const estMin = Number(p.estimatedMinutesOnSite || 0) || Math.round(Number(p.estimatedHours || 0) * 60)
      totalStops += 1
      totalServiceMin += estMin

      const dateOverride = effectiveCrewByProperty.get(Number(p.aspirePropertyId))
      const rid = dateOverride?.routeId || p.assignedRouteId
      const cid = dateOverride?.crewId || p.assignedCrewId
      const key = rid ? String(rid) : (cid ? String(cid) : null)
      if (!key) {
        unassignedStops += 1
        unassignedServiceMin += estMin
        return
      }
      const existing = crewDayMap.get(key)
      if (existing) {
        existing.stops += 1
        existing.serviceMin += estMin
      } else {
        crewDayMap.set(key, {
          routeId: rid || null,
          crewId: cid || null,
          routeName: nameById.get(String(rid || cid)) || p.assignedRouteName || p.assignedCrewName || key,
          stops: 1,
          serviceMin: estMin
        })
      }
    })

    const crewDays = Array.from(crewDayMap.values()).sort((a, b) => b.serviceMin - a.serviceMin)
    return {
      dayName: dayName || '',
      date: focusDate,
      crewCount: crewDays.length,
      totalStops,
      totalServiceMin,
      unassignedStops,
      unassignedServiceMin,
      crewDays
    }
  }, [isFleetMode, focusDate, propertyFeatures, effectiveCrewByProperty, nameById])

  const activeSecondaryFilterCount = useMemo(() => {
    let count = 0
    if (showVipOnly) count++
    if (showAnchorsOnly) count++
    if (showLockedOnly) count++
    if (!showCentroids) count++
    if (contextWeekType !== 'ALL') count++
    if (frequencyFocus !== 'all') count++
    if (showDueOnly) count++
    if (routeFilterId) count++
    if (isFleetMode && !fleetTodayOnly) count++
    if (isFleetMode && !showFleetRoutes) count++
    if (!isFleetMode && focusCrewId && !showRouteStops) count++
    if (showUnassigned) count++
    if (showIrregularMtc) count++
    return count
  }, [
    contextWeekType,
    fleetTodayOnly,
    focusCrewId,
    frequencyFocus,
    isFleetMode,
    routeFilterId,
    showAnchorsOnly,
    showCentroids,
    showDueOnly,
    showFleetRoutes,
    showIrregularMtc,
    showLockedOnly,
    showRouteStops,
    showUnassigned,
    showVipOnly
  ])

  const resetMapControls = useCallback((options = {}) => {
    const includeUnassigned = !!options.includeUnassigned
    setShowVipOnly(false)
    setShowAnchorsOnly(false)
    setShowLockedOnly(false)
    setShowCentroids(true)
    setShowDueOnly(false)
    setShowRouteStops(true)
    setShowUnassigned(includeUnassigned)
    setShowIrregularMtc(false)
    setFleetTodayOnly(true)
    setShowFleetRoutes(true)
    setRouteFilterId('')
    setContextWeekType('ALL')
    setFrequencyFocus('all')
  }, [])

  useEffect(() => {
    if (reviewRequest?.type !== 'cut_crew_unassigned') return
    fetchMapData()
    resetMapControls()
    setFocusRouteId('__ALL__')
    setFocusCrewId('')
    setRouteFilterId('unassigned')
    setLegendOpen(true)
    if (onSnackbar) {
      const count = reviewRequest.summary?.unassignedCount
      const countText = Number.isFinite(Number(count)) ? `${count} ` : ''
      onSnackbar(`${countText}cut-crew stops are now unassigned for review`, 'info')
    }
  }, [fetchMapData, onSnackbar, resetMapControls, reviewRequest])

  const focusRouteCapacityCell = useCallback((route, dayIdx) => {
    resetMapControls()
    if (route?.crewId) setFocusCrewId(route.crewId)
    if (route?.routeId) setFocusRouteId(route.routeId)
    if (dayIdx >= 0 && capacityMatrix?.weekOf) {
      const weekStart = new Date(`${capacityMatrix.weekOf}T00:00:00`)
      const targetDate = new Date(weekStart)
      targetDate.setDate(weekStart.getDate() + dayIdx)
      setFocusDate(toYmd(targetDate))
    }
  }, [capacityMatrix?.weekOf, resetMapControls])

  const focusRouteFromChip = useCallback((id) => {
    const routeOrCrewId = String(id || '')
    if (!routeOrCrewId) return

    const route = availableRoutes.find(r => String(r.routeId) === routeOrCrewId) ||
      availableRoutes.find(r => String(r.crewId) === routeOrCrewId)

    if (route) {
      setFocusRouteId(String(route.routeId))
      setFocusCrewId(route.crewId ? String(route.crewId) : '')
    } else {
      setFocusRouteId('')
      setFocusCrewId(routeOrCrewId)
    }

    setShowRouteStops(true)
    setRouteFilterId('')
  }, [availableRoutes])

  const getMarkerStyle = useCallback((feature) => {
    const props = feature.properties
    // Use date-specific crew/route from futureVisitCrews when available
    const dateOverride = effectiveCrewByProperty.get(Number(props.aspirePropertyId))
    const effectiveRouteId = dateOverride?.routeId || props.assignedRouteId
    const effectiveCrewId = dateOverride?.crewId || props.assignedCrewId
    const crewColor = effectiveRouteId
      ? getRouteColor(effectiveRouteId, routeIds)
      : (effectiveCrewId ? getCrewColor(effectiveCrewId, crewIds) : '#9E9E9E')

    const isSelected = Number(props.aspirePropertyId) === Number(selectedPropertyId)
    const isOnRoute = routeStopIds.size > 0 && routeStopIds.has(Number(props.aspirePropertyId))
    const hasActiveRoute = routeStopIds.size > 0

    const baseScale = props.isAnchor ? 12 : (props.isVip ? 10 : 8)
    let scale = isSelected ? baseScale + 4 : (isOnRoute ? baseScale + 2 : baseScale)
    let strokeColor = isSelected ? '#1976D2' : (isOnRoute ? '#00BCD4' : (props.isLocked ? '#FFD600' : '#ffffff'))
    let strokeWeight = isSelected ? 4 : (isOnRoute ? 3 : (props.isLocked ? 3 : 1.5))
    let fillOpacity = isSelected ? 1 : (hasActiveRoute && !isOnRoute ? 0.45 : 0.85)

    return {
      radius: scale,
      fillColor: crewColor,
      fillOpacity,
      color: strokeColor,
      weight: strokeWeight,
      opacity: 1
    }
  }, [crewIds, effectiveCrewByProperty, routeIds, routeStopIds, selectedPropertyId])

  const onMapLoad = useCallback((map) => {
    setMapRef(map)
  }, [])

  const handlePropertySelect = useCallback((feature) => {
    if (!feature || feature.properties?.featureType !== 'property') return
    setSelectedPropertyFallbackFeature(feature)
    setSelectedPropertyId(Number(feature.properties.aspirePropertyId))
  }, [])

  const openRouteStopPropertyDetails = useCallback((stop) => {
    const aspirePropertyId = Number(stop?.aspirePropertyId)
    if (!Number.isFinite(aspirePropertyId)) return
    const existing = allPropertyFeatures.find(f => Number(f.properties?.aspirePropertyId) === aspirePropertyId)
    if (existing) {
      handlePropertySelect(existing)
      setPropertyDetailOpen(true)
      return
    }
    const lat = Number(stop.latitude ?? stop.lat)
    const lng = Number(stop.longitude ?? stop.lng)
    const routeName = isRouteMode
      ? (stop.routeName || availableRoutes.find(r => r.routeId === focusRouteId)?.routeName || '')
      : ''
    const crewName = isRouteMode
      ? routeName
      : (stop.crewName || availableCrews.find(item => item.crewId === focusCrewId)?.crewName || '')
    handlePropertySelect({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [Number.isFinite(lng) ? lng : 0, Number.isFinite(lat) ? lat : 0]
      },
      properties: {
        featureType: 'property',
        aspirePropertyId,
        propertyName: stop.propertyName || stop.label || `Aspire #${aspirePropertyId}`,
        address: stop.address || '',
        assignedRouteName: routeName,
        assignedCrewName: crewName,
        assignedRouteId: stop.routeId || (isRouteMode ? focusRouteId : null),
        assignedCrewId: stop.crewId || focusCrewId || null,
        estimatedMinutesOnSite: stop.serviceMinutes || stop.serviceMin || 0,
        prescribedManHours: stop.prescribedManHours ?? null,
        medianOnSiteMinutes: stop.medianServiceMinutes || 0,
        dayOfWeek: stop.dayName || stop.dayOfWeek || '',
        futureVisits: stop.date ? [{ scheduledDate: stop.date, routeName, crewName }] : [],
        _routeStopOnly: true
      }
    })
    setPropertyDetailOpen(true)
  }, [allPropertyFeatures, availableCrews, availableRoutes, focusCrewId, focusRouteId, handlePropertySelect, isRouteMode])

  const handleMapPropertySelect = useCallback((feature) => {
    handlePropertySelect(feature)
    if (planningMode === 'week' || planningMode === 'two-week') {
      setPropertyDetailOpen(true)
    }
  }, [handlePropertySelect, planningMode])

  // Helper: post a move and handle 409 conflicts (approved route day, locked property) with confirmation dialog + retry
  const postMoveWithApprovalCheck = async (moveUrl, payload) => {
    try {
      const resp = await axios.post(moveUrl, payload)
      return resp
    } catch (err) {
      if (err.response?.status === 409 && err.response?.data?.code === 'APPROVED_ROUTE_DAY') {
        const data = err.response.data
        const confirmed = await requestConfirmation(
          'Approved Route Day',
          data.message || 'This move affects an approved route day.',
          'approved',
          { fromApproved: data.fromApproved, toApproved: data.toApproved }
        )
        if (!confirmed) {
          if (onSnackbar) onSnackbar('Move cancelled — approved route was not changed', 'info')
          throw new Error('Move cancelled by user')
        }
        const resp = await axios.post(moveUrl, { ...payload, force: true })
        fetchApprovedRouteDays()
        return resp
      }
      if (err.response?.status === 409 && err.response?.data?.code === 'LOCKED_PROPERTY') {
        const lockedName = err.response.data.lockedCrewName || 'another crew'
        const confirmed = await requestConfirmation(
          'Locked Property',
          `This property is locked to ${lockedName}.`,
          'locked',
          { lockedCrewName: lockedName }
        )
        if (!confirmed) {
          if (onSnackbar) onSnackbar('Move cancelled — locked property was not changed', 'info')
          throw new Error('Move cancelled by user')
        }
        return axios.post(moveUrl, { ...payload, force: true })
      }
      throw err
    }
  }

  const moveSelectedProperty = async () => {
    if (!selectedScenarioId) {
      if (onSnackbar) onSnackbar('Pick a draft scenario first to move properties', 'warning')
      return
    }

    if (!selectedPropertyFeature) return

    if (!hasAnyMoveChange) {
      if (onSnackbar) onSnackbar('No assignment or schedule change to apply', 'info')
      return
    }

    setApplyingMove(true)
    try {
      const aspirePropertyId = Number(selectedPropertyFeature.properties?.aspirePropertyId)
      const payload = {
        aspirePropertyId,
        moveReason: 'map_manual'
      }
      if (isRouteMode) {
        payload.toRouteId = assignmentRouteId || null
      } else {
        payload.toCrewId = assignmentRouteId || null
      }
      if (hasDayUpdate) payload.toDaysOfWeek = scheduleDay ? [scheduleDay] : []
      if (hasWeekTypeUpdate) payload.toWeekType = scheduleWeekType === 'ANY' ? null : scheduleWeekType

      const resp = await postMoveWithApprovalCheck(`${url}/api/hmi/territory/scenarios/${selectedScenarioId}/move`, payload)
      const snapCount = (resp?.data?.snapshotsAffected?.pulled || 0) + (resp?.data?.snapshotsAffected?.pushed || 0)
      const snapSuffix = snapCount > 0 ? ` (${snapCount} snapshots updated)` : ''

      if (onSnackbar) {
        const toName = assignmentRouteId
          ? (assignmentOptions.find(o => o.id === assignmentRouteId)?.name || 'route')
          : 'Unassigned'
        if (hasCrewUpdate && (hasDayUpdate || hasWeekTypeUpdate)) {
          onSnackbar(`Assignment + schedule updated (${toName})` + snapSuffix, 'success')
        } else if (hasCrewUpdate) {
          onSnackbar(
            (assignmentRouteId
              ? `Property reassigned to ${toName}`
              : 'Property unassigned') + snapSuffix,
            'success'
          )
        } else {
          onSnackbar('Property schedule updated' + snapSuffix, 'success')
        }
      }

      setScheduleDay('')
      setScheduleWeekType('')

      await Promise.all([
        fetchMapData(),
        fetchScenarioMetrics(),
        fetchFocusContextStats(),
        fetchFocusedRoute()
      ])
    } catch (err) {
      if (err.message === 'Move cancelled by user') { setApplyingMove(false); return }
      console.error('Error moving property from map:', err)
      if (onSnackbar) onSnackbar(err.response?.data?.message || err.message || 'Failed to move property', 'error')
    } finally {
      setApplyingMove(false)
    }
  }

  const assignAsAnchor = async (isAnchor) => {
    if (!selectedPropertyFeature) return
    if (isAnchor && !selectedWeeklyAnchorEligible) {
      if (onSnackbar) onSnackbar(`Only weekly properties can be anchors (this is ${selectedFrequencyLabel})`, 'warning')
      return
    }

    const propertyId = Number(selectedPropertyFeature.properties?.aspirePropertyId)
    if (isAnchor && !anchorCrewId) {
      if (onSnackbar) onSnackbar('Choose an anchor crew first', 'warning')
      return
    }

    setApplyingAnchor(true)
    try {
      await axios.patch(`${url}/api/hmi/territory/anchors/${propertyId}`, {
        isAnchor: !!isAnchor,
        anchorCrewId: isAnchor ? anchorCrewId : null
      })
      if (onSnackbar) onSnackbar(isAnchor ? 'Anchor assigned' : 'Anchor removed', 'success')
      await fetchMapData()
    } catch (err) {
      console.error('Error updating anchor from map:', err)
      if (onSnackbar) onSnackbar(err.response?.data?.message || err.message || 'Failed to update anchor', 'error')
    } finally {
      setApplyingAnchor(false)
    }
  }

  const suggestNearestCrew = async () => {
    if (!selectedScenarioId || !selectedPropertyFeature) {
      if (onSnackbar) onSnackbar('Select a draft scenario to use nearest-crew suggestion', 'warning')
      return
    }

    const aspirePropertyId = Number(selectedPropertyFeature.properties?.aspirePropertyId)

    setSuggestingCrew(true)
    try {
      const response = await axios.post(`${url}/api/hmi/territory/scenarios/${selectedScenarioId}/suggest-nearest`, {
        aspirePropertyIds: [aspirePropertyId],
        includeAssigned: true,
        limit: 1
      })
      const suggestion = response.data?.suggestions?.[0] || null
      setNearestCrewSuggestion(suggestion)

      const suggestedId = suggestion?.suggestedRouteId || suggestion?.suggestedCrewId
      const suggestedName = suggestion?.suggestedRouteName || suggestion?.suggestedCrewName

      if (suggestedId) {
        setAssignmentRouteId(String(suggestedId))
        if (!anchorCrewId) setAnchorCrewId(String(suggestedId))

        const distLabel = Number.isFinite(Number(suggestion.driveMinutes))
          ? `${Math.round(Number(suggestion.driveMinutes))} min`
          : `${Number(suggestion.distanceMiles || 0).toFixed(2)} mi`

        if (onSnackbar) onSnackbar(`Suggested ${suggestedName || 'route'} (${distLabel})`, 'success')
      } else if (onSnackbar) {
        onSnackbar('No nearest route suggestion available', 'info')
      }
    } catch (err) {
      console.error('Error suggesting nearest crew from map:', err)
      if (onSnackbar) onSnackbar(err.response?.data?.message || err.message || 'Failed to suggest nearest crew', 'error')
    } finally {
      setSuggestingCrew(false)
    }
  }

  // Phase 3C: Service time helper (uses hoursMode toggle)
  // All times (property-level and crew-level) are already wall-clock minutes (pre-divided by crew size on server).
  const getServiceMinutes = useCallback((props) => {
    if (!props) return 0
    if (hoursMode === 'actual' && (props.medianOnSiteMinutes || props.medianOnSiteHours)) {
      // medianOnSiteMinutes is wall-clock (man-hours ÷ crew size), computed on server
      return props.medianOnSiteMinutes || Math.round((props.medianOnSiteHours || 0) * 60)
    }
    // estimatedMinutesOnSite is already wall-clock time per property
    return Number(props.estimatedMinutesOnSite || 0) || Math.round((props.estimatedHours || 0) * 60)
  }, [hoursMode])

  const selectedProps = selectedPropertyFeature?.properties || null
  const selectedPropertyDetailBase = useMemo(
    () => selectedPropertyFeature ? adaptMapFeatureToRoutingProperty(selectedPropertyFeature) : null,
    [selectedPropertyFeature]
  )
  const selectedPropertyDetail = hydratedPropertyDetail || selectedPropertyDetailBase
  const selectedPropertyDetailId = selectedPropertyDetailBase?.aspirePropertyId || selectedPropertyDetailBase?.propertyId || null
  const selectedDateOverride = selectedProps
    ? effectiveCrewByProperty.get(Number(selectedProps.aspirePropertyId))
    : null
  const selectedEffectiveRouteId = selectedDateOverride?.routeId || selectedProps?.assignedRouteId || null
  const selectedEffectiveCrewId = selectedDateOverride?.crewId || selectedProps?.assignedCrewId || null
  const selectedAssignedId = selectedEffectiveRouteId
    ? String(selectedEffectiveRouteId)
    : (selectedEffectiveCrewId ? String(selectedEffectiveCrewId) : '')
  const selectedIsRouteUnlinked = !!selectedEffectiveRouteId && !selectedEffectiveCrewId
  const selectedEffectiveName = selectedDateOverride
    ? (nameById.get(String(selectedEffectiveRouteId)) || nameById.get(String(selectedEffectiveCrewId)) || selectedProps?.assignedRouteName || selectedProps?.assignedCrewName || 'Unassigned')
    : (selectedProps?.assignedRouteName || selectedProps?.assignedCrewName || 'Unassigned')
  const selectedServiceMinutes = getServiceMinutes(selectedProps)
  const focusRouteName = isRouteMode
    ? (availableRoutes.find(r => r.routeId === focusRouteId)?.routeName || 'Route')
    : (availableCrews.find(item => item.crewId === focusCrewId)?.crewName || 'Crew')
  const focusCrewName = focusRouteName

  useEffect(() => {
    setHydratedPropertyDetail(null)
    if (!selectedPropertyFeature) {
      setPropertyDetailOpen(false)
    }
  }, [selectedPropertyFeature])

  useEffect(() => {
    if (!propertyDetailOpen || !selectedPropertyDetailBase || !selectedPropertyDetailId || !url) return

    let cancelled = false
    axios.get(`${url}/api/aspire/routing/properties/${selectedPropertyDetailId}`, {
      params: selectedScenarioId ? { scenarioId: selectedScenarioId } : {}
    })
      .then(response => {
        if (cancelled || !response.data) return
        setHydratedPropertyDetail(normalizeRoutingRow({
          ...selectedPropertyDetailBase,
          ...response.data,
          name: response.data.name || response.data.propertyName || selectedPropertyDetailBase.name,
          propertyId: response.data.propertyId || response.data.aspirePropertyId || response.data.PropertyID || selectedPropertyDetailBase.propertyId,
          aspirePropertyId: response.data.aspirePropertyId || response.data.propertyId || response.data.PropertyID || selectedPropertyDetailBase.aspirePropertyId,
          latitude: response.data.latitude ?? selectedPropertyDetailBase.latitude,
          longitude: response.data.longitude ?? selectedPropertyDetailBase.longitude
        }))
      })
      .catch(() => {}) // The drawer can still render from the selected map feature.

    return () => {
      cancelled = true
    }
  }, [propertyDetailOpen, selectedPropertyDetailBase, selectedPropertyDetailId, selectedScenarioId, url])

  // Phase 4D: "Add to Day" action for route builder
  const addToDay = async (aspirePropertyId) => {
    if (!selectedScenarioId || !focusCrewId || !focusDate) {
      if (onSnackbar) onSnackbar('Select a scenario, crew, and date first', 'warning')
      return
    }
    const dayName = getDayName(parseYmd(focusDate))
    if (!dayName) return
    const candidate = routeBuilderCandidates.find(item => Number(item.aspirePropertyId) === Number(aspirePropertyId))
    setRouteActionPending(true)
    try {
      const movePayload = {
        aspirePropertyId: Number(aspirePropertyId),
        toDaysOfWeek: [dayName],
        moveReason: 'route_builder_add'
      }
      // If we can resolve focused crew to a route, send toRouteId
      const focusRoute = availableRoutes.find(r => r.crewId === focusCrewId)
      if (focusRoute) {
        movePayload.toRouteId = focusRoute.routeId
      } else {
        movePayload.toCrewId = focusCrewId
      }
      const resp = await postMoveWithApprovalCheck(`${url}/api/hmi/territory/scenarios/${selectedScenarioId}/move`, movePayload)
      const snapCount = (resp?.data?.snapshotsAffected?.pulled || 0) + (resp?.data?.snapshotsAffected?.pushed || 0)
      const snapSuffix = snapCount > 0 ? ` (${snapCount} snapshots updated)` : ''
      const occurrenceSuffix = candidate?.occurrenceCount > 1 ? ` (${candidate.occurrenceCount} occurrences)` : ''
      if (onSnackbar) onSnackbar(`Added to ${focusCrewName}'s ${dayName} route${occurrenceSuffix}` + snapSuffix, 'success')
      await Promise.all([
        fetchMapData(),
        fetchScenarioMetrics(),
        fetchFocusContextStats(),
        fetchFocusedRoute()
      ])
    } catch (err) {
      if (err.message === 'Move cancelled by user') { setRouteActionPending(false); return }
      console.error('Error adding property to day route:', err)
      if (onSnackbar) onSnackbar(err.response?.data?.message || err.message || 'Failed to add property', 'error')
    } finally {
      setRouteActionPending(false)
    }
  }

  // Unassign a property from its current crew (set crew to unassigned)
  const unassignProperty = async (aspirePropertyId) => {
    if (!selectedScenarioId || !aspirePropertyId) {
      if (onSnackbar) onSnackbar('Select a scenario first', 'warning')
      return
    }
    setRouteActionPending(true)
    try {
      const resp = await postMoveWithApprovalCheck(`${url}/api/hmi/territory/scenarios/${selectedScenarioId}/move`, {
        aspirePropertyId: Number(aspirePropertyId),
        toCrewId: 'unassigned',
        toDaysOfWeek: [],
        moveReason: 'route_builder_unassign'
      })
      const snapCount = (resp?.data?.snapshotsAffected?.pulled || 0) + (resp?.data?.snapshotsAffected?.pushed || 0)
      const snapSuffix = snapCount > 0 ? ` (${snapCount} snapshots updated)` : ''
      if (onSnackbar) onSnackbar('Property unassigned' + snapSuffix, 'success')
      setSelectedPropertyId(null)
      await Promise.all([
        fetchMapData(),
        fetchScenarioMetrics(),
        fetchFocusContextStats(),
        fetchFocusedRoute()
      ])
    } catch (err) {
      if (err.message === 'Move cancelled by user') { setRouteActionPending(false); return }
      console.error('Error unassigning property:', err)
      if (onSnackbar) onSnackbar(err.response?.data?.message || err.message || 'Failed to unassign', 'error')
    } finally {
      setRouteActionPending(false)
    }
  }

  // Best Fit: fetch suggestions when unassigned property selected
  const fetchBestFit = useCallback(async (propId) => {
    if (!selectedScenarioId || !propId) return
    setBestFitLoading(true)
    try {
      const resp = await axios.post(`${url}/api/hmi/territory/scenarios/${selectedScenarioId}/best-fit`, {
        aspirePropertyId: Number(propId),
        weekOf: focusDate || toYmd(new Date()),
        limit: 10
      })
      setBestFitData(resp.data)
      if (resp.data?.capacityMatrix) setCapacityMatrix(resp.data.capacityMatrix)
    } catch (err) {
      console.error('Best fit error:', err)
      setBestFitData(null)
    } finally {
      setBestFitLoading(false)
    }
  }, [url, selectedScenarioId, focusDate])

  useEffect(() => {
    if (!selectedPropertyId) {
      setBestFitData(null)
      return
    }
    // Only auto-fetch for unassigned properties
    const feature = allPropertyFeatures.find(f => Number(f.properties?.aspirePropertyId) === Number(selectedPropertyId))
    if (!feature) { setBestFitData(null); return }
    const props = feature.properties || {}
    const dateOvr = effectiveCrewByProperty.get(Number(props.aspirePropertyId))
    const effRouteId = dateOvr?.routeId || props.assignedRouteId
    const effCrewId = dateOvr?.crewId || props.assignedCrewId
    if (effRouteId || effCrewId) {
      setBestFitData(null)
      return
    }
    fetchBestFit(selectedPropertyId)
  }, [selectedPropertyId, allPropertyFeatures, effectiveCrewByProperty, fetchBestFit])

  // Best Fit: handle assign action
  const bestFitAssign = async (suggestion) => {
    if (!selectedScenarioId || !suggestion) return
    const aspirePropertyId = Number(selectedPropertyFeature?.properties?.aspirePropertyId)
    if (!aspirePropertyId) return
    setRouteActionPending(true)
    try {
      const payload = {
        aspirePropertyId,
        moveReason: 'best_fit_assign',
        toRouteId: suggestion.routeId,
        toDaysOfWeek: [suggestion.dayOfWeek]
      }
      await postMoveWithApprovalCheck(`${url}/api/hmi/territory/scenarios/${selectedScenarioId}/move`, payload)
      if (onSnackbar) onSnackbar(`Assigned to ${suggestion.routeName} — ${suggestion.dayOfWeek}`, 'success')
      setBestFitData(null)
      await Promise.all([
        fetchMapData(),
        fetchScenarioMetrics(),
        fetchFocusContextStats(),
        fetchFocusedRoute()
      ])
    } catch (err) {
      if (err.message === 'Move cancelled by user') { setRouteActionPending(false); return }
      console.error('Best fit assign error:', err)
      if (onSnackbar) onSnackbar(err.response?.data?.message || err.message || 'Failed to assign', 'error')
    } finally {
      setRouteActionPending(false)
    }
  }

  // Check if the selected property is on the focused route
  // Also verify the property is still assigned to the focused crew (avoids stale "On route"
  // chip after a reassign/unassign when focusedRoute hasn't refreshed yet)
  const selectedIsOnRoute = useMemo(() => {
    if (!selectedProps?.aspirePropertyId || !focusedRoute?.stops?.length || !focusCrewId) return false
    const inStops = focusedRoute.stops.some(s => Number(s.aspirePropertyId) === Number(selectedProps.aspirePropertyId))
    if (!inStops) return false
    // Confirm effective assignment still points at the focused crew
    const effCrewId = selectedEffectiveCrewId ? String(selectedEffectiveCrewId) : ''
    const effRouteId = selectedEffectiveRouteId ? String(selectedEffectiveRouteId) : ''
    if (!effCrewId && !effRouteId) return false // unassigned
    // Match by crewId or by routeId belonging to the focused crew
    if (effCrewId === focusCrewId) return true
    const focusRoute = availableRoutes.find(r => r.crewId === focusCrewId)
    if (focusRoute && effRouteId === focusRoute.routeId) return true
    return false
  }, [selectedProps, focusedRoute, focusCrewId, selectedEffectiveCrewId, selectedEffectiveRouteId, availableRoutes])

  const selectedFrequencyCode = String(selectedProps?.frequencyCode || '').trim().toUpperCase()
  const selectedFrequencyLabel = selectedProps?.frequencyLabel || getFrequencyLabelFromCode(selectedFrequencyCode)
  const selectedWeeklyAnchorEligible = selectedProps
    ? (typeof selectedProps.weeklyAnchorEligible === 'boolean'
      ? selectedProps.weeklyAnchorEligible
      : selectedFrequencyCode === 'W')
    : false
  const selectedFutureMaintenanceVisits = useMemo(() => {
    if (!selectedProps) return []
    const today = toYmd(new Date())
    return (Array.isArray(selectedProps.futureVisitCrews) ? selectedProps.futureVisitCrews : [])
      .map(visit => {
        const date = String(visit.date || '').slice(0, 10)
        if (!date) return null
        const serviceCategory = String(visit.serviceCategory || 'maintenance').toLowerCase()
        if (serviceCategory && serviceCategory !== 'maintenance') return null
        return {
          ...visit,
          date,
          dayOfWeek: visit.dayOfWeek || getDayName(parseYmd(date))
        }
      })
      .filter(visit => visit && visit.date >= today)
      .sort((a, b) => a.date.localeCompare(b.date))
  }, [selectedProps])
  const selectedMaintenanceDays = useMemo(() => {
    const futureVisitDays = selectedFutureMaintenanceVisits
      .map(v => v.dayOfWeek)
      .filter(Boolean)
    const days = futureVisitDays.length
      ? futureVisitDays
      : (Array.isArray(selectedProps?.daysOfWeek) ? selectedProps.daysOfWeek : [])
    return [...new Set(days)]
      .filter(Boolean)
      .sort((a, b) => WORK_DAYS.indexOf(a) - WORK_DAYS.indexOf(b))
  }, [selectedFutureMaintenanceVisits, selectedProps])

  const focusedRoutePath = useMemo(() => {
    const coords = focusedRoute?.route?.geometry?.coordinates || []
    return coords
      .map(item => {
        const [lng, lat] = item || []
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
        return { lat, lng }
      })
      .filter(Boolean)
  }, [focusedRoute])

  // Fit bounds when crew, date, or fleet mode changes — includes route paths and yard
  const prevViewRef = useRef('')
  useEffect(() => {
    const viewKey = `${focusCrewId}|${focusRouteId}|${focusDate}`
    if (!mapRef) return
    if (viewKey === prevViewRef.current) return

    // Small delay so route data has time to arrive after crew/date change
    const timer = setTimeout(() => {
      const bounds = L.latLngBounds([])
      propertyFeatures.forEach(f => {
        const [lng, lat] = f.geometry.coordinates
        if (Number.isFinite(lat) && Number.isFinite(lng)) bounds.extend([lat, lng])
      })
      // Include focused crew route path
      if (focusedRoutePath.length > 1 && !isFleetMode) {
        focusedRoutePath.forEach(pt => bounds.extend([pt.lat, pt.lng]))
      }
      // Include fleet route paths
      if (isFleetMode && showFleetRoutes) {
        fleetRoutes.forEach(fr => fr.path.forEach(pt => bounds.extend([pt.lat, pt.lng])))
      }
      if (bounds.isValid()) {
        mapRef.invalidateSize()
        mapRef.fitBounds(bounds, { padding: [50, 50] })
        prevViewRef.current = viewKey
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [mapRef, focusCrewId, focusRouteId, focusDate, propertyFeatures, focusedRoutePath, isFleetMode, showFleetRoutes, fleetRoutes])

  // Phase 4B: Remaining capacity (how many minutes left before 8h OT threshold)
  const remainingCapacityMin = useMemo(() => {
    if (!focusContextStats?.summary) return DAILY_OT_THRESHOLD_MIN
    const svcMin = Math.round((focusContextStats.summary.serviceMin || 0))
    const driveMin = focusContextStats.summary.driveMin || 0
    return Math.max(0, DAILY_OT_THRESHOLD_MIN - svcMin - driveMin)
  }, [focusContextStats])

  // Phase 4A: Route builder candidates
  const routeBuilderCandidates = useMemo(() => {
    if (!focusCrewId || !focusDate || !focusedRoute?.stops?.length) return []

    const routeStops = focusedRoute.stops || []
    const onRouteIds = new Set(routeStops.map(s => Number(s.aspirePropertyId)).filter(Number.isFinite))

    return allPropertyFeatures
      .filter(f => !onRouteIds.has(Number(f.properties?.aspirePropertyId)))
      .map(f => {
        const p = f.properties || {}
        const [lng, lat] = f.geometry?.coordinates || []
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null

        // Distance to nearest route stop
        let minDist = Infinity
        routeStops.forEach(stop => {
          if (Number.isFinite(stop.latitude) && Number.isFinite(stop.longitude)) {
            const d = haversineMiles(lat, lng, stop.latitude, stop.longitude)
            if (d < minDist) minDist = d
          }
        })

        const svcMinutes = getServiceMinutes(p)
        const estimatedMin = Number(p.estimatedMinutesOnSite || 0) || Math.round((p.estimatedHours || 0) * 60)
        const actualMin = p.medianOnSiteHours ? Math.round(p.medianOnSiteHours * 60) : null
        const delta = actualMin != null ? actualMin - estimatedMin : null
        const dueToday = filteredContextDates.length ? filteredContextDates.some(date => isDueOnDate(p, date)) : false
        const targetDayName = getDayName(parseYmd(focusDate))
        const previewRow = targetDayName
          ? {
              ...p,
              daysOfWeek: [targetDayName],
              preferredDay: targetDayName,
              preferredDays: [targetDayName]
            }
          : p
        const impactedDays = targetDayName
          ? filteredContextDates
            .filter(date => getDayName(date) === targetDayName && isDueOnDate(previewRow, date))
            .map(date => {
              const dateStr = toYmd(date)
              const current = focusContextStats?.days?.find(day => day.date === dateStr) || {}
              const currentTotalMin = Number(current.totalMin || 0)
              const currentOtMin = Number(current.otMin || 0)
              const projectedTotalMin = currentTotalMin + svcMinutes
              const projectedOtMin = Math.max(0, projectedTotalMin - DAILY_OT_THRESHOLD_MIN)
              return {
                date: dateStr,
                weekType: getWeekTypeForDate(date),
                currentTotalMin,
                currentOtMin,
                projectedTotalMin,
                projectedOtMin
              }
            })
          : []
        const wouldCreateOt = impactedDays.some(day => day.projectedOtMin > day.currentOtMin)

        // Look up assigned crew's day stats for capacity indicator (use date-specific if available)
        const dateOvr = effectiveCrewByProperty.get(Number(p.aspirePropertyId))
        const assignedRid = String(dateOvr?.routeId || p.assignedRouteId || '')
        const assignedCid = String(dateOvr?.crewId || p.assignedCrewId || '')
        const crewDayStats = capacityByRoute.get(assignedRid || null) || capacityByRoute.get(assignedCid || null) || null
        const effName = dateOvr
          ? (nameById.get(assignedRid) || nameById.get(assignedCid) || p.assignedRouteName || p.assignedCrewName || 'Unassigned')
          : (p.assignedRouteName || p.assignedCrewName || 'Unassigned')

        return {
          aspirePropertyId: Number(p.aspirePropertyId),
          propertyName: p.propertyName,
          address: p.address,
          assignedName: effName,
          frequencyCode: p.frequencyCode,
          serviceMinutes: svcMinutes,
          delta,
          distanceMiles: minDist === Infinity ? null : Math.round(minDist * 100) / 100,
          dueToday,
          impactedDays,
          occurrenceCount: impactedDays.length || (dueToday ? 1 : 0),
          wouldCreateOt,
          crewDayStats,
          feature: f
        }
      })
      .filter(c => c && c.distanceMiles != null)
      .sort((a, b) => a.distanceMiles - b.distanceMiles)
      .slice(0, 30)
  }, [allPropertyFeatures, capacityByRoute, effectiveCrewByProperty, filteredContextDates, focusCrewId, focusContextStats, focusDate, focusedRoute, getServiceMinutes, nameById])

  const visibleSuggestionCandidates = useMemo(() => {
    if (!showSuggestionPins) return []
    return candidatesDueOnly
      ? routeBuilderCandidates.filter(c => c.dueToday)
      : routeBuilderCandidates
  }, [candidatesDueOnly, routeBuilderCandidates, showSuggestionPins])

  const selectedCurrentDays = selectedProps ? normalizeDaysForRow(selectedProps) : []
  const selectedCurrentWeekType = selectedProps ? rowWeekType(selectedProps) || null : null
  const selectedNextDays = scheduleDay ? [scheduleDay] : selectedCurrentDays
  const selectedNextWeekType = scheduleWeekType === 'ANY'
    ? null
    : (scheduleWeekType || selectedCurrentWeekType || null)
  const selectedPreviewRow = useMemo(() => {
    if (!selectedProps) return null
    return {
      ...selectedProps,
      daysOfWeek: selectedNextDays,
      preferredDay: scheduleDay || selectedProps.preferredDay || null,
      preferredDays: scheduleDay ? [scheduleDay] : (selectedProps.preferredDays || selectedNextDays),
      weekType: selectedNextWeekType
    }
  }, [scheduleDay, selectedNextDays, selectedNextWeekType, selectedProps])
  const hasCrewUpdate = !!selectedProps && String(selectedAssignedId || '') !== String(assignmentRouteId || '')
  const hasDayUpdate = !!selectedProps && !!scheduleDay && !areSameStringArrays(selectedCurrentDays, selectedNextDays)
  const hasWeekTypeUpdate = !!selectedProps && (
    scheduleWeekType === 'ANY'
      ? Boolean(selectedCurrentWeekType)
      : Boolean(scheduleWeekType && scheduleWeekType !== selectedCurrentWeekType)
  )
  const hasAnyMoveChange = hasCrewUpdate || hasDayUpdate || hasWeekTypeUpdate
  const pendingChanges = useMemo(() => {
    const changes = []
    if (hasCrewUpdate) {
      const fromName = assignmentOptions.find(o => o.id === selectedAssignedId)?.name || 'Unassigned'
      const toName = assignmentOptions.find(o => o.id === assignmentRouteId)?.name || 'Unassigned'
      changes.push(`Route: ${fromName} -> ${toName}`)
    }
    if (hasDayUpdate) {
      changes.push(`Day: ${(selectedCurrentDays.join(', ') || 'none')} -> ${(selectedNextDays.join(', ') || 'none')}`)
    }
    if (hasWeekTypeUpdate) {
      changes.push(`Week type: ${selectedCurrentWeekType || 'Any'} -> ${selectedNextWeekType || 'Any'}`)
    }
    return changes
  }, [
    assignmentOptions,
    assignmentRouteId,
    hasCrewUpdate,
    hasDayUpdate,
    hasWeekTypeUpdate,
    selectedAssignedId,
    selectedCurrentDays,
    selectedCurrentWeekType,
    selectedNextDays,
    selectedNextWeekType
  ])

  const weekDatesForBaseline = useMemo(() => {
    const weekStart = getMonday(parseYmd(focusDate))
    return WORK_DAYS.map((_, idx) => addDays(weekStart, idx))
  }, [focusDate])

  const currentOccurrencesInContext = useMemo(
    () => (selectedProps ? countDueOccurrences(selectedProps, filteredContextDates) : 0),
    [filteredContextDates, selectedProps]
  )
  const projectedOccurrencesInContext = useMemo(
    () => (selectedPreviewRow ? countDueOccurrences(selectedPreviewRow, filteredContextDates) : 0),
    [filteredContextDates, selectedPreviewRow]
  )
  const currentOccurrencesInWeek = useMemo(
    () => (selectedProps ? countDueOccurrences(selectedProps, weekDatesForBaseline) : 0),
    [selectedProps, weekDatesForBaseline]
  )
  const projectedOccurrencesInWeek = useMemo(
    () => (selectedPreviewRow ? countDueOccurrences(selectedPreviewRow, weekDatesForBaseline) : 0),
    [selectedPreviewRow, weekDatesForBaseline]
  )

  // When 'actual' mode, recompute service from route stops' medianServiceMinutes
  const displayContextStats = useMemo(() => {
    if (!focusContextStats?.summary) return null
    const base = focusContextStats.summary
    if (hoursMode !== 'actual' || !focusedRoute?.stops?.length) return base

    // Sum median service minutes from route stops (already wall-clock)
    let actualServiceMin = 0
    focusedRoute.stops.forEach(stop => {
      if (stop.medianServiceMinutes > 0) {
        actualServiceMin += stop.medianServiceMinutes
      } else {
        // Fallback to estimated (already wall-clock minutes, pre-divided by crew size on server)
        actualServiceMin += Math.round(stop.serviceMinutes || 0)
      }
    })
    const driveMin = base.driveMin || 0
    const totalMin = actualServiceMin + driveMin
    const otMin = Math.max(0, totalMin - DAILY_OT_THRESHOLD_MIN)
    return { ...base, serviceMin: actualServiceMin, totalMin, otMin }
  }, [focusContextStats, focusedRoute, hoursMode])

  const weeklyDisplayStats = useMemo(() => {
    if (!weeklyCrewStats) return null
    const isActual = hoursMode === 'actual'

    // Override the focused day's values with displayContextStats so the chip
    // always matches the daily stats shown directly above in the Route Context card.
    let weekTotalMin = 0
    const days = weeklyCrewStats.days.map(d => {
      const isFocusedDay = d.date === focusDate
      let displayTotal, displayOt
      if (isFocusedDay && displayContextStats) {
        displayTotal = displayContextStats.totalMin || 0
        displayOt = displayContextStats.otMin || 0
      } else {
        displayTotal = isActual ? d.actualTotalMin : d.totalMin
        displayOt = isActual ? d.actualOtMin : d.otMin
      }
      weekTotalMin += displayTotal
      return { ...d, displayTotal, displayOt }
    })
    const weekOtMin = Math.max(0, weekTotalMin - WEEKLY_OT_THRESHOLD_MIN)
    return { weekTotalMin, weekOtMin, days }
  }, [weeklyCrewStats, hoursMode, focusDate, displayContextStats])

  const twoWeekDisplayStats = useMemo(() => {
    if (planningMode !== 'two-week' || !focusContextStats?.days?.length) return null

    const groups = ['A', 'B'].map(weekType => {
      const days = focusContextStats.days
        .filter(day => day.weekType === weekType)
        .sort((a, b) => WORK_DAYS.indexOf(a.dayName) - WORK_DAYS.indexOf(b.dayName))

      const weekTotalMin = days.reduce((sum, day) => sum + Number(day.totalMin || 0), 0)
      const weekServiceMin = days.reduce((sum, day) => sum + Number(day.serviceMin || 0), 0)
      const weekDriveMin = days.reduce((sum, day) => sum + Number(day.driveMin || 0), 0)
      const weekOtMin = Math.max(0, weekTotalMin - WEEKLY_OT_THRESHOLD_MIN)
      const dayOtMin = days.reduce((sum, day) => sum + Number(day.otMin || 0), 0)

      return {
        weekType,
        days,
        weekTotalMin,
        weekServiceMin,
        weekDriveMin,
        weekOtMin,
        dayOtMin,
        hasOt: weekOtMin > 0 || dayOtMin > 0
      }
    })

    const totalMin = groups.reduce((sum, group) => sum + group.weekTotalMin, 0)
    const totalOtMin = groups.reduce((sum, group) => sum + group.weekOtMin + group.dayOtMin, 0)
    return { groups, totalMin, totalOtMin }
  }, [focusContextStats, planningMode])

  const handleWeekDaySelect = useCallback((date) => {
    if (!date) return
    if (date !== focusDate) setFocusDate(date)
    setPlanningMode('day')
  }, [focusDate])

  const contextSummary = displayContextStats || null
  const contextThresholdMinutes = Math.max(DAILY_OT_THRESHOLD_MIN, DAILY_OT_THRESHOLD_MIN * (filteredContextDates.length || 1))
  const currentServiceInContext = selectedServiceMinutes * currentOccurrencesInContext
  const projectedServiceInContext = selectedServiceMinutes * projectedOccurrencesInContext
  const weeklyDeltaForCrew = (crewId, targetCrewId = assignmentRouteId) => {
    if (!selectedProps || !crewId) return 0
    const currentContribution = String(selectedAssignedId || '') === String(crewId)
      ? selectedServiceMinutes * currentOccurrencesInWeek
      : 0
    const projectedContribution = String(targetCrewId || '') === String(crewId)
      ? selectedServiceMinutes * projectedOccurrencesInWeek
      : 0
    return projectedContribution - currentContribution
  }
  const focusCrewServiceDelta = (() => {
    if (!selectedProps || !focusCrewId) return 0
    const currentContribution = String(selectedAssignedId || '') === String(focusCrewId || '')
      ? selectedServiceMinutes * currentOccurrencesInContext
      : 0
    const projectedContribution = String(assignmentRouteId || '') === String(focusCrewId || '')
      ? selectedServiceMinutes * projectedOccurrencesInContext
      : 0
    return projectedContribution - currentContribution
  })()
  const projectedFocusTotalMinutes = contextSummary
    ? Math.max(0, Number(contextSummary.totalMin || 0) + focusCrewServiceDelta)
    : null
  const projectedFocusOtMinutes = projectedFocusTotalMinutes != null
    ? Math.max(0, projectedFocusTotalMinutes - contextThresholdMinutes)
    : null

  const fromCrewMetrics = selectedAssignedId ? crewMetricsById.get(selectedAssignedId) || null : null
  const toCrewMetrics = assignmentRouteId ? crewMetricsById.get(String(assignmentRouteId)) || null : null

  const fromCrewProjectedMinutes = fromCrewMetrics
    ? Math.max(0, Number(fromCrewMetrics.weeklyTotalMinutes || 0) + weeklyDeltaForCrew(selectedAssignedId, assignmentRouteId))
    : null
  const toCrewProjectedMinutes = toCrewMetrics
    ? Math.max(0, Number(toCrewMetrics.weeklyTotalMinutes || 0) + weeklyDeltaForCrew(assignmentRouteId, assignmentRouteId))
    : null

  // Build crewId→routeName lookup for display
  const routeNameByCrewId = useMemo(() => {
    const map = new Map()
    availableRoutes.forEach(r => { if (r.crewId) map.set(r.crewId, r.routeName) })
    return map
  }, [availableRoutes])

  const candidateCrews = useMemo(() => {
    if (!selectedScenarioId) return []
    if (!selectedPropertyFeature) return []
    const [selectedLng, selectedLat] = selectedPropertyFeature.geometry.coordinates || []
    if (!Number.isFinite(selectedLat) || !Number.isFinite(selectedLng)) return []

    return availableCrews
      .map(crew => {
        const centroid = centroidByCrewId.get(String(crew.crewId))
        if (!centroid) return null

        const distanceMiles = haversineMiles(selectedLat, selectedLng, centroid.lat, centroid.lng)
        const metrics = crewMetricsById.get(String(crew.crewId)) || null
        const baseTotal = Number(metrics?.weeklyTotalMinutes || 0)
        const projectedTotal = Math.max(0, baseTotal + weeklyDeltaForCrew(String(crew.crewId), String(crew.crewId)))
        const projectedOt = Math.max(0, projectedTotal - WEEKLY_OT_THRESHOLD_MIN)
        const projectedDelta = projectedTotal - baseTotal

        return {
          crewId: String(crew.crewId),
          crewName: crew.crewName,
          routeName: routeNameByCrewId.get(String(crew.crewId)) || crew.crewName,
          distanceMiles,
          baseTotal,
          projectedTotal,
          projectedOt,
          projectedDelta
        }
      })
      .filter(Boolean)
      .sort((a, b) => a.distanceMiles - b.distanceMiles)
      .slice(0, 6)
  }, [
    availableCrews,
    centroidByCrewId,
    crewMetricsById,
    currentOccurrencesInWeek,
    projectedOccurrencesInWeek,
    routeNameByCrewId,
    selectedAssignedId,
    selectedPropertyFeature,
    selectedScenarioId,
    selectedServiceMinutes
  ])

  // Auto-optimize action handlers — confirm-then-act, adapted to MapTab's
  // generic requestConfirmation(title, message, type, details, options) dialog.
  // Copy is lifted verbatim from RefineRouteTab so the confirm text is identical.
  const handleAutoOptimizeMenuPick = useCallback(async (mode) => {
    autoOpt.setAutoOptMenuAnchor(null)
    if (mode === 'balanced') {
      const ok = await requestConfirmation(
        'Auto-optimize (balanced)?',
        'Globally reassign every unlocked stop across the INCLUDED maintenance routes to minimize drive time, keeping each crew-day under 8 hours. Locked stops and excluded crews are untouched. Changes apply to this scenario and are logged as moves.',
        'generic', null,
        { confirmLabel: 'Auto-optimize', cancelLabel: 'Cancel' }
      )
      if (ok) autoOpt.startAutoOptimize('balanced')
    } else {
      const ok = await requestConfirmation(
        'Consolidate crew-days?',
        'Pack work into as FEW crew-days as possible: days may run up to 9 hours if that finishes a route, freeing whole days elsewhere (candidates for cutting a day). Weeks that exceed 40 hours are flagged in the report. Changes apply to this scenario and are logged as moves.',
        'generic', null,
        { confirmLabel: 'Consolidate', cancelLabel: 'Cancel' }
      )
      if (ok) autoOpt.startAutoOptimize('consolidate')
    }
  }, [autoOpt, requestConfirmation])

  const handleAutoOptimizeButtonClick = useCallback(async (event) => {
    if (autoOpt.autoRunActive) {
      const ok = await requestConfirmation(
        'Cancel auto-optimize?',
        'Stop the in-flight optimization run? Nothing will be applied.',
        'generic', null,
        { confirmLabel: 'Stop run', cancelLabel: 'Keep running' }
      )
      if (ok) autoOpt.cancelAutoOptimize()
      return
    }
    autoOpt.setAutoOptMenuAnchor(event.currentTarget)
  }, [autoOpt, requestConfirmation])

  return (
    <div style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
      {/* Stats strip at the top */}
      <MapStatsStrip stats={fleetDayStats} propertyCount={meta?.propertyCount} />

      {/* In-flow toolbar above the map */}
      <div style={{ position: 'relative', flexShrink: 0 }}>
        <MapToolbar
          isRouteMode={isRouteMode}
          isFleetMode={isFleetMode}
          focusCrewId={focusCrewId}
          setFocusCrewId={setFocusCrewId}
          focusRouteId={focusRouteId}
          setFocusRouteId={setFocusRouteId}
          focusDate={focusDate}
          setFocusDate={setFocusDate}
          planningMode={planningMode}
          setPlanningMode={setPlanningMode}
          contextWeekType={contextWeekType}
          setContextWeekType={setContextWeekType}
          availableRoutes={availableRoutes}
          availableCrews={availableCrews}
          showFleetRoutes={showFleetRoutes}
          setShowFleetRoutes={setShowFleetRoutes}
          fleetRoutesLoading={fleetRoutesLoading}
          showRouteStops={showRouteStops}
          setShowRouteStops={setShowRouteStops}
          showUnassigned={showUnassigned}
          setShowUnassigned={setShowUnassigned}
          fleetTodayOnly={fleetTodayOnly}
          setFleetTodayOnly={setFleetTodayOnly}
          showIrregularMtc={showIrregularMtc}
          setShowIrregularMtc={setShowIrregularMtc}
          routeFilterId={routeFilterId}
          setRouteFilterId={setRouteFilterId}
          resetMapControls={resetMapControls}
          filterAnchorEl={filterAnchorEl}
          setFilterAnchorEl={setFilterAnchorEl}
          showVipOnly={showVipOnly}
          setShowVipOnly={setShowVipOnly}
          showAnchorsOnly={showAnchorsOnly}
          setShowAnchorsOnly={setShowAnchorsOnly}
          showLockedOnly={showLockedOnly}
          setShowLockedOnly={setShowLockedOnly}
          showCentroids={showCentroids}
          setShowCentroids={setShowCentroids}
          showDueOnly={showDueOnly}
          setShowDueOnly={setShowDueOnly}
          frequencyFocus={frequencyFocus}
          setFrequencyFocus={setFrequencyFocus}
          activeSecondaryFilterCount={activeSecondaryFilterCount}
          fetchMapData={fetchMapData}
          loading={loading}
          loadingMetrics={loadingMetrics}
          loadingFocusContextStats={loadingFocusContextStats}
          loadingFocusedRoute={loadingFocusedRoute}
          propertyFeatures={propertyFeatures}
          legendOpen={legendOpen}
          setLegendOpen={setLegendOpen}
          routeLegend={routeLegend}
          unassignedCount={unassignedCount}
          routeIds={routeIds}
          crewIds={crewIds}
          focusRouteFromChip={focusRouteFromChip}
          reviewNavOpen={reviewNavOpen}
          setReviewNavOpen={setReviewNavOpen}
          reviewCombos={reviewCombos}
          filteredReviewCombos={filteredReviewCombos}
          reviewComboIdx={reviewComboIdx}
          setReviewComboIdx={setReviewComboIdx}
          reviewNavNext={reviewNavNext}
          reviewNavPrev={reviewNavPrev}
          navigateToCombo={navigateToCombo}
          approvedRouteDays={approvedRouteDays}
          skipApproved={skipApproved}
          setSkipApproved={setSkipApproved}
          selectedScenarioId={selectedScenarioId}
          isScenarioEditable={isScenarioEditable}
          autoOptimize={{
            active: autoOpt.autoRunActive,
            run: autoOpt.autoRun,
            onButtonClick: handleAutoOptimizeButtonClick,
            onMenuPick: handleAutoOptimizeMenuPick,
            menuAnchor: autoOpt.autoOptMenuAnchor,
            setMenuAnchor: autoOpt.setAutoOptMenuAnchor,
            placeUnassigned: autoOpt.placeUnassigned,
            setPlaceUnassigned: autoOpt.setPlaceUnassigned,
            onOpenReport: () => autoOpt.openRunReport(autoOpt.autoRun),
            timeAuditLoading: autoOpt.timeAudit.loading,
            onOpenTimeAudit: autoOpt.openTimeAudit
          }}
          longCycle={{
            open: longCycleOpen,
            onToggle: () => setLongCycleOpen(open => !open)
          }}
        />
      </div>

      {/* Map + sidebar — wrapped in WeekVizProvider so its overlays & sidebar
          can share state across the two render slots. */}
      <ConditionalWeekVizProvider
        enabled={planningMode === 'week' || planningMode === 'two-week'}
        providerProps={{
          mapRef, url, focusDate, focusCrewId, isFleetMode, planningMode,
          availableCrews, onSnackbar, onStopClick: openRouteStopPropertyDetails
        }}
      >
      <div style={{ flex: 1, minHeight: 0, display: 'flex', overflow: 'hidden' }}>
        {/* ── Map area ────────────────────────────────────────── */}
        <div style={{ flex: 1, minHeight: 0, position: 'relative', overflow: 'hidden' }}>
          <TerritoryLeafletMap
            propertyFeatures={visibleMapPropertyFeatures}
            selectedPropertyId={selectedPropertyId}
            suggestionCandidates={planningMode === 'day' ? visibleSuggestionCandidates : []}
            showSuggestionPins={planningMode === 'day' && showSuggestionPins}
            focusedRouteStops={planningMode === 'day' ? (focusedRoute?.stops || []) : []}
            focusedRoutePath={planningMode === 'day' ? focusedRoutePath : []}
            isFleetMode={planningMode === 'day' && isFleetMode}
            showFleetRoutes={planningMode === 'day' && showFleetRoutes}
            fleetRoutes={planningMode === 'day' ? fleetRoutes : []}
            centroidFeatures={planningMode === 'day' ? centroidFeatures : []}
            routeIds={routeIds}
            crewIds={crewIds}
            getMarkerStyle={getMarkerStyle}
            handlePropertySelect={handleMapPropertySelect}
            setRouteFilterId={setRouteFilterId}
            onMapLoad={onMapLoad}
          />

          {onOpenRefine && !isFleetMode && focusRouteId && focusRouteId !== '__ALL__' && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => onOpenRefine(focusRouteId)}
              title="Open this route in the Crew Schedule Planner"
              style={{
                position: 'absolute',
                top: 12,
                right: 12,
                zIndex: 1000,
                fontSize: 12,
                padding: '7px 12px',
                boxShadow: '0 4px 14px rgba(0,0,0,0.25)'
              }}
            >
              Refine this route →
            </button>
          )}

          {/* Capacity drawer scrim + drawer — day mode only */}
          {planningMode === 'day' && drawerOpen && <div className="cap-drawer-scrim" style={{ zIndex: 2999 }} onClick={() => setDrawerOpen(false)} />}
          {planningMode === 'day' && drawerOpen && (
            <CapacityDrawer
              capacityMatrix={capacityMatrix}
              capacityLoading={capacityLoading}
              approvedRouteDays={approvedRouteDays}
              approvedSet={approvedSet}
              focusRouteCapacityCell={focusRouteCapacityCell}
              focusedRoute={focusedRoute}
              contextSummary={contextSummary}
              weeklyDisplayStats={weeklyDisplayStats}
              hoursMode={hoursMode}
              setHoursMode={setHoursMode}
              focusCrewName={focusCrewName}
              focusDate={focusDate}
              onOpenPropertyDetails={openRouteStopPropertyDetails}
              onClose={() => setDrawerOpen(false)}
            />
          )}

          {/* Long-cycle planner scrim + drawer — any planning mode (live-level data) */}
          {longCycleOpen && <div className="cap-drawer-scrim" style={{ zIndex: 2999 }} onClick={() => setLongCycleOpen(false)} />}
          {longCycleOpen && (
            <LongCyclePlannerDrawer
              url={url}
              scenarioId={selectedScenarioId || null}
              onSnackbar={onSnackbar}
              onClose={() => setLongCycleOpen(false)}
            />
          )}

          {/* ── WeekViz overlays (trace lines, legends, color toggle) ── */}
          {(planningMode === 'week' || planningMode === 'two-week') && (
            <WeekVizOverlays />
          )}
        </div>

        {/* ── Route sidebar (376px) — day mode only ───────── */}
        {planningMode === 'day' && (
          <RouteSidebar
            isFleetMode={isFleetMode}
            focusDate={focusDate}
            focusCrewId={focusCrewId}
            focusRouteId={focusRouteId}
            focusCrewName={focusCrewName}
            hoursMode={hoursMode}
            setHoursMode={setHoursMode}
            setFocusRouteId={setFocusRouteId}
            setFocusCrewId={setFocusCrewId}
            fleetDayStats={fleetDayStats}
            liveFleetDayStats={liveFleetDayStats}
            capacityMatrix={capacityMatrix}
            loadingFocusContextStats={loadingFocusContextStats}
            focusContextStats={focusContextStats}
            contextSummary={contextSummary}
            contextThresholdMinutes={contextThresholdMinutes}
            twoWeekDisplayStats={twoWeekDisplayStats}
            weeklyDisplayStats={weeklyDisplayStats}
            planningMode={planningMode}
            selectedProps={selectedProps}
            selectedAssignedId={selectedAssignedId}
            focusCrewServiceDelta={focusCrewServiceDelta}
            projectedFocusTotalMinutes={projectedFocusTotalMinutes}
            projectedFocusOtMinutes={projectedFocusOtMinutes}
            handleWeekDaySelect={handleWeekDaySelect}
            focusedRoute={focusedRoute}
            mapRef={mapRef}
            setSelectedPropertyId={setSelectedPropertyId}
            fetchFocusContextStats={fetchFocusContextStats}
            fetchFocusedRoute={fetchFocusedRoute}
            url={url}
            selectedScenarioId={selectedScenarioId}
            approvedSet={approvedSet}
            setApprovedRouteDays={setApprovedRouteDays}
            onSnackbar={onSnackbar}
            routeBuilderCandidates={routeBuilderCandidates}
            candidatesDueOnly={candidatesDueOnly}
            setCandidatesDueOnly={setCandidatesDueOnly}
            showSuggestionPins={showSuggestionPins}
            setShowSuggestionPins={setShowSuggestionPins}
            remainingCapacityMin={remainingCapacityMin}
            routeActionPending={routeActionPending}
            handlePropertySelect={handlePropertySelect}
            addToDay={addToDay}
            routeIds={routeIds}
            crewIds={crewIds}
            SUGGESTION_PIN_COLORS={SUGGESTION_PIN_COLORS}
            setDrawerOpen={setDrawerOpen}
            propertyCount={meta?.propertyCount || 0}
            routeCount={meta?.routeCount || meta?.crewCount || 0}
            propertyDetailsSlot={selectedProps ? (
              <div className="route-property-panel">
                <div className="route-property-panel-header">
                  <span>Property Details</span>
                  <div className="route-property-panel-actions">
                    <button
                      type="button"
                      className="route-property-detail-button"
                      onClick={() => setPropertyDetailOpen(true)}
                    >
                      <OpenInNewIcon style={{ fontSize: 13 }} />
                      Full details
                    </button>
                    <button
                      type="button"
                      className="route-property-close"
                      onClick={() => {
                        setPropertyDetailOpen(false)
                        setSelectedPropertyFallbackFeature(null)
                        setSelectedPropertyId(null)
                      }}
                      aria-label="Close property details"
                    >
                      x
                    </button>
                  </div>
                </div>
                <PropertyPopover
                  selectedProps={selectedProps}
                  selectedAssignedId={selectedAssignedId}
                  selectedEffectiveName={selectedEffectiveName}
                  selectedFrequencyLabel={selectedFrequencyLabel}
                  selectedMaintenanceDays={selectedMaintenanceDays}
                  selectedIsOnRoute={selectedIsOnRoute}
                  selectedIsRouteUnlinked={selectedIsRouteUnlinked}
                  focusCrewId={focusCrewId}
                  focusDate={focusDate}
                  focusCrewName={focusCrewName}
                  routeIds={routeIds}
                  crewIds={crewIds}
                  selectedScenarioId={selectedScenarioId}
                  routeActionPending={routeActionPending}
                  unassignProperty={unassignProperty}
                  addToDay={addToDay}
                  assignmentOptions={assignmentOptions}
                  assignmentRouteId={assignmentRouteId}
                  setAssignmentRouteId={setAssignmentRouteId}
                  moveSelectedProperty={moveSelectedProperty}
                  hasAnyMoveChange={hasAnyMoveChange}
                  pendingChanges={pendingChanges}
                  applyingMove={applyingMove}
                  suggestNearestCrew={suggestNearestCrew}
                  suggestingCrew={suggestingCrew}
                  bestFitData={bestFitData}
                  bestFitLoading={bestFitLoading}
                  bestFitOpen={bestFitOpen}
                  setBestFitOpen={setBestFitOpen}
                  bestFitAssign={bestFitAssign}
                />
              </div>
            ) : null}
          />
        )}

        {/* ── WeekViz sidebar — week / two-week modes ─────── */}
        {(planningMode === 'week' || planningMode === 'two-week') && (
          <WeekVizSidebar />
        )}
      </div>
      </ConditionalWeekVizProvider>

      <PropertyDetailDrawer
        open={propertyDetailOpen && !!selectedPropertyDetail}
        onClose={() => setPropertyDetailOpen(false)}
        property={selectedPropertyDetail}
        url={url}
        allRows={allPropertyDetailRows}
      />

      <AutoOptimizeRunReport
        open={autoOpt.runReportOpen}
        onClose={() => autoOpt.setRunReportOpen(false)}
        run={autoOpt.autoRun}
        moves={autoOpt.runMoves}
        movesLoading={autoOpt.runMovesLoading}
        url={url}
        scenarioId={selectedScenarioId}
        onEliminationApplied={handleAutoOptApplied}
      />

      <TimeAuditModal
        open={autoOpt.timeAudit.open}
        onClose={() => autoOpt.setTimeAudit(prev => ({ ...prev, open: false }))}
        loading={autoOpt.timeAudit.loading}
        suggestions={autoOpt.timeAudit.suggestions}
        meta={autoOpt.timeAudit.meta}
        onApply={autoOpt.applyTimeAuditRow}
        applyingIds={autoOpt.auditApplyingIds}
      />

      {/* Confirmation dialog for approved-route / locked-property overrides */}
      <Dialog open={confirmDialog.open} onClose={() => handleConfirmDialogClose(false)} maxWidth="sm" fullWidth>
        <DialogTitle style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {confirmDialog.type === 'locked'
            ? <LockIcon style={{ color: '#e65100' }} />
            : <WarningIcon style={{ color: '#e65100' }} />}
          {confirmDialog.title || 'Confirm'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" style={{ marginBottom: 12 }}>
            {confirmDialog.message}
          </Typography>

          {confirmDialog.type === 'approved' && (
            <Box style={{ background: '#fff3e0', borderRadius: 4, padding: '10px 14px', marginBottom: 12 }}>
              <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 4 }}>
                This will affect ALL future snapshots, not just today.
              </Typography>
              <Typography variant="body2">
                {confirmDialog.details?.fromApproved && confirmDialog.details?.toApproved
                  ? 'Both the source and destination route days are approved. Moving will modify finalized routes on both sides across all matching dates.'
                  : confirmDialog.details?.fromApproved
                    ? 'The source route day is approved. Removing this property will modify a finalized route across all matching dates.'
                    : 'The destination route day is approved. Adding this property will modify a finalized route across all matching dates.'}
              </Typography>
              <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 6 }}>
                If you only want to change a single day, cancel and edit that day's snapshot directly.
              </Typography>
            </Box>
          )}

          {confirmDialog.type === 'locked' && (
            <Box style={{ background: '#fff3e0', borderRadius: 4, padding: '10px 14px', marginBottom: 12 }}>
              <Typography variant="body2">
                This property is assigned to a specific crew. Overriding the lock will move it and update all matching future snapshots.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions style={{ padding: '8px 24px 16px' }}>
          <Button onClick={() => handleConfirmDialogClose(false)}>
            {confirmDialog.options?.cancelLabel || 'Cancel'}
          </Button>
          <Button
            variant="contained"
            style={{ backgroundColor: '#e65100', color: '#fff' }}
            onClick={() => handleConfirmDialogClose(true)}
          >
            {confirmDialog.options?.confirmLabel || 'Move Anyway'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}
